/**
 * GENERATED FILE — DO NOT EDIT.
 * Run `pnpm a2a-contract:generate` after changing the
 * canonical Pydantic contract.
 */
export const contractSchema = {
  $defs: {
    AgentConfig: {
      additionalProperties: false,
      description:
        "How the agent reasons + loops (reused by every future skill).",
      properties: {
        execution: {
          $ref: "#/$defs/ExecutionConfig",
          description:
            "Loop iteration and wall-clock safety bounds for each turn.",
        },
        model: {
          description:
            "Provider-discriminated computer-use model and generation limits.",
          discriminator: {
            mapping: {
              anthropic: "#/$defs/AnthropicModelConfig",
              gemini: "#/$defs/GeminiModelConfig",
              openai: "#/$defs/OpenAIModelConfig",
            },
            propertyName: "provider",
          },
          oneOf: [
            {
              $ref: "#/$defs/AnthropicModelConfig",
            },
            {
              $ref: "#/$defs/OpenAIModelConfig",
            },
            {
              $ref: "#/$defs/GeminiModelConfig",
            },
          ],
          title: "Model",
        },
        resultSchema: {
          anyOf: [
            {
              additionalProperties: true,
              type: "object",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Optional JSON Schema that the done tool's caller-defined structured result must satisfy; null requests only a free-text summary.",
          title: "Resultschema",
        },
        visualOnly: {
          default: false,
          description:
            "Whether the agent must reason from screenshots alone, without DOM, accessibility-tree, or selector-based perception.",
          title: "Visualonly",
          type: "boolean",
        },
      },
      required: ["model"],
      title: "AgentConfig",
      type: "object",
    },
    AiAttribution: {
      additionalProperties: false,
      description:
        'The envelope a caller sends on every ai-service request.\n\nBoth blocks default to empty, so ``{"version": 1}`` is a valid minimal envelope —\na caller with no identity in scope still declares the contract version rather than\nsending something unparseable.',
      properties: {
        correlation: {
          $ref: "#/$defs/AiAttributionCorrelation",
          description:
            "Per-run ids for logs and traces. Never emitted as spend tags.",
        },
        tags: {
          $ref: "#/$defs/AiAttributionTags",
          description: "Dimensions that may become LiteLLM spend tags.",
        },
        version: {
          const: 1,
          default: 1,
          description:
            "Envelope version. Bumped only for a rename, retype, or removal; adding an optional field is additive. The literal states the version this schema describes; a higher version is mapped onto it by the boundary decoder (`usage_tags._with_supported_version`) after unknown fields are stripped, so a newer caller degrades to less attribution rather than to an error.",
          title: "Version",
          type: "integer",
        },
      },
      title: "AiAttribution",
      type: "object",
    },
    AiAttributionCorrelation: {
      additionalProperties: false,
      description:
        "Per-run ids for logs and traces. **Never** emitted as LiteLLM tags.\n\nCarried so that a consumer wanting per-run forensics has the ids without a second\nchannel. Nothing in this service turns these into tags, and nothing should: each\nis unbounded per day, which is precisely the cardinality that would make the spend\naggregates unusable and un-cleanable.",
      properties: {
        agentRunId: {
          anyOf: [
            {
              maxLength: 64,
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "`ai_agent_runs.id` this work belongs to.",
          title: "Agentrunid",
        },
        requestId: {
          anyOf: [
            {
              maxLength: 64,
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Caller's request id, for correlating logs across the hop.",
          title: "Requestid",
        },
        stepRunId: {
          anyOf: [
            {
              maxLength: 64,
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "`wfv2_step_runs.id` this work belongs to.",
          title: "Steprunid",
        },
        taskId: {
          anyOf: [
            {
              maxLength: 64,
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Task the work was spawned for, when a task loop drove it.",
          title: "Taskid",
        },
        traceId: {
          anyOf: [
            {
              maxLength: 64,
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Caller's trace id. Distinct from W3C `traceparent`, which rides its own key.",
          title: "Traceid",
        },
        workflowRunId: {
          anyOf: [
            {
              maxLength: 64,
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "`wfv2_workflow_runs.id` this work belongs to.",
          title: "Workflowrunid",
        },
      },
      title: "AiAttributionCorrelation",
      type: "object",
    },
    AiAttributionTags: {
      additionalProperties: false,
      description:
        "Bounded-cardinality dimensions that may be emitted as LiteLLM tags.\n\nEvery id here is the **most specific row id that exists**; names, versions, and\nlineage are resolved at read time by the same batched lookups that produce\ndisplay labels. That keeps one tag per dimension while still supporting both\nversioned and rolled-up views.\n\nAdding a field is additive and needs no version bump. Adding a *per-run* id is\nforbidden — see the module docstring.",
      properties: {
        agentDefinitionId: {
          anyOf: [
            {
              maxLength: 64,
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "`ai_agent_definitions.id`. Unversioned: the table has a `version` column that nothing increments and agent runs record no version, so this is a lineage id only. Distinct from `sopVersionId`, which is genuinely versioned.",
          title: "Agentdefinitionid",
        },
        feature: {
          anyOf: [
            {
              maxLength: 64,
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Free label for call sites with no row to point at — e.g. `job:<name>` for scheduled work. The catch-all, so it stays low cardinality by convention.",
          title: "Feature",
        },
        module: {
          anyOf: [
            {
              maxLength: 64,
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Owning product module. Not constrained to an enum here on purpose: the canonical `ProductModule` list lives in `@erp/shared-types` and mirroring it would create a second unchecked copy. The sender is typed against it.",
          title: "Module",
        },
        nodeId: {
          anyOf: [
            {
              maxLength: 64,
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "`wfv2_workflow_steps.id`. Step rows belong to a workflow version, so this value churns on republish; that is bounded per day and accepted. The step type comes from the read-time join, not a second tag.",
          title: "Nodeid",
        },
        organizationId: {
          anyOf: [
            {
              maxLength: 64,
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "`um_organizations.id` the work is on behalf of.",
          title: "Organizationid",
        },
        skill: {
          anyOf: [
            {
              $ref: "#/$defs/SkillId",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "A2A skill that served the request. Set by the ai-service, not the caller.",
        },
        sopVersionId: {
          anyOf: [
            {
              maxLength: 64,
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "`AgentSopVersion.id` — the operator procedure that ran. Separate from `agentDefinitionId` because SOPs version properly and background agents do not.",
          title: "Sopversionid",
        },
        userId: {
          anyOf: [
            {
              maxLength: 64,
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "`um_users.id` who triggered the work, or the literal `system` for known unattended work. Absent means the caller did not know, which is a different fact and emits no `user:` tag.",
          title: "Userid",
        },
        workflowDefinitionId: {
          anyOf: [
            {
              maxLength: 64,
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "`wfv2_workflow_definitions.id`. That row IS a workflow version, and it carries `workflowId` to the lineage row owning the display name — so this one id yields both the per-version and rolled-up views.",
          title: "Workflowdefinitionid",
        },
      },
      title: "AiAttributionTags",
      type: "object",
    },
    AnnotationPoint: {
      additionalProperties: false,
      description:
        "One viewport-independent point in normalized live-view coordinates.",
      properties: {
        x: {
          description: "Horizontal position from 0 (left) to 1 (right).",
          maximum: 1,
          minimum: 0,
          title: "X",
          type: "number",
        },
        y: {
          description: "Vertical position from 0 (top) to 1 (bottom).",
          maximum: 1,
          minimum: 0,
          title: "Y",
          type: "number",
        },
      },
      required: ["x", "y"],
      title: "AnnotationPoint",
      type: "object",
    },
    AnnotationResume: {
      additionalProperties: false,
      allOf: [
        {
          anyOf: [
            {
              properties: {
                note: {
                  description:
                    "A non-blank textual explanation satisfies the annotation guidance requirement.",
                  pattern:
                    "[^\\u0009-\\u000d\\u001c-\\u0020\\u0085\\u00a0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000]",
                  type: "string",
                },
              },
              required: ["note"],
            },
            {
              properties: {
                strokes: {
                  description:
                    "At least one visual mark satisfies the annotation guidance requirement.",
                  items: {
                    $ref: "#/$defs/AnnotationStroke",
                  },
                  minItems: 1,
                  type: "array",
                },
              },
              required: ["strokes"],
            },
          ],
        },
      ],
      description: "Continue SOP authoring with marked-up visual guidance.",
      properties: {
        kind: {
          const: "annotation",
          description: "Resume-directive discriminator.",
          title: "Kind",
          type: "string",
        },
        note: {
          default: "",
          description: "Optional textual explanation accompanying the marks.",
          maxLength: 2000,
          title: "Note",
          type: "string",
        },
        strokes: {
          description:
            "Semantically labelled marks drawn over the current browser frame.",
          items: {
            $ref: "#/$defs/AnnotationStroke",
          },
          maxItems: 50,
          title: "Strokes",
          type: "array",
        },
      },
      required: ["kind"],
      title: "AnnotationResume",
      type: "object",
    },
    AnnotationStroke: {
      additionalProperties: false,
      description:
        "One semantically labelled screen annotation drawn by the human.",
      properties: {
        intent: {
          description: "Meaning assigned to the mark by the human.",
          enum: ["act_here", "avoid", "note"],
          title: "Intent",
          type: "string",
        },
        kind: {
          default: "freehand",
          description:
            "Geometry used to interpret the mark and its target point.",
          enum: ["freehand", "circle", "arrow"],
          title: "Kind",
          type: "string",
        },
        points: {
          description: "Ordered normalized points comprising the mark.",
          items: {
            $ref: "#/$defs/AnnotationPoint",
          },
          minItems: 1,
          title: "Points",
          type: "array",
        },
      },
      required: ["intent", "points"],
      title: "AnnotationStroke",
      type: "object",
    },
    AnthropicModelConfig: {
      additionalProperties: false,
      description:
        "Anthropic computer-use model selection and generation limits.",
      properties: {
        maxTokens: {
          default: 16384,
          description:
            "Maximum output-token budget for each computer-use model turn.",
          minimum: 1,
          title: "Maxtokens",
          type: "integer",
        },
        modelId: {
          description: "Allow-listed Anthropic computer-use model identifier.",
          enum: [
            "claude-sonnet-5-5",
            "claude-opus-5-5",
            "claude-sonnet-5",
            "claude-opus-5",
            "claude-sonnet-4-6",
            "claude-opus-4-7",
            "claude-opus-4-8",
            "claude-haiku-5-5",
          ],
          title: "Modelid",
          type: "string",
        },
        provider: {
          const: "anthropic",
          default: "anthropic",
          description: "Provider discriminator selecting the Anthropic driver.",
          title: "Provider",
          type: "string",
        },
        thinkingBudget: {
          default: 1024,
          description:
            "Requested extended-thinking token budget; currently consumed by Anthropic and ignored by providers without an equivalent control.",
          minimum: 0,
          title: "Thinkingbudget",
          type: "integer",
        },
      },
      required: ["modelId"],
      title: "AnthropicModelConfig",
      type: "object",
    },
    ApplicationCredentialRef: {
      additionalProperties: false,
      description:
        "A named in-desktop application credential (e.g. Famous) referenced BY ID (AI-294).\n\nValues never cross the wire — the operator resolves each ``credential_id`` Python-side and\ntypes it via the keyboard-based ``fill_credential`` tool. ``name`` is the human label the\noperator matches against (shown in the tool's description; non-secret).",
      properties: {
        credentialId: {
          description:
            "Opaque credential reference resolved server-side; the secret value never crosses the A2A contract.",
          title: "Credentialid",
          type: "string",
        },
        name: {
          description:
            "Non-secret human label the operator uses to select this application credential.",
          minLength: 1,
          pattern:
            "[^\\u0009-\\u000d\\u001c-\\u0020\\u0085\\u00a0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000]",
          title: "Name",
          type: "string",
        },
      },
      required: ["name", "credentialId"],
      title: "ApplicationCredentialRef",
      type: "object",
    },
    AuthorSopArtifact: {
      description: "Human-readable or structured completed SOP deliverable.",
      discriminator: {
        mapping: {
          result: "#/$defs/ResultTextArtifact",
          result_json: "#/$defs/AuthorSopResultArtifact",
        },
        propertyName: "name",
      },
      oneOf: [
        {
          $ref: "#/$defs/ResultTextArtifact",
        },
        {
          $ref: "#/$defs/AuthorSopResultArtifact",
        },
      ],
    },
    AuthorSopInput: {
      additionalProperties: false,
      description:
        "Initial configuration for the interactive SOP-authoring skill.",
      properties: {
        agent: {
          $ref: "#/$defs/AgentConfig",
          description:
            "Provider, model, output contract, and execution bounds for the SOP-authoring agent.",
        },
        brief: {
          $ref: "#/$defs/BriefConfig",
          description:
            "Optional title and objective seed; omitted values are discovered interactively.",
        },
        freeRoam: {
          default: false,
          description:
            "Whether to park for human-led browser demonstrations that are captured on explicit resume turns instead of running the classic agent-led step interview.",
          title: "Freeroam",
          type: "boolean",
        },
        pausePolicy: {
          $ref: "#/$defs/PausePolicy",
          default: "high_risk_only",
          description:
            "Autonomy policy controlling which actions require human confirmation during SOP authoring.",
        },
        session: {
          $ref: "#/$defs/SessionConfig",
          description:
            "Caller-owned browser session, credentials, and display-input configuration used during the interview.",
        },
      },
      required: ["session", "agent"],
      title: "AuthorSopInput",
      type: "object",
    },
    AuthorSopOutput: {
      additionalProperties: false,
      description: "Final structured SOP authored by the interview.",
      properties: {
        branches: {
          description: "Captured alternate-path declarations.",
          items: {
            $ref: "#/$defs/AuthoredTrajectoryPath",
          },
          title: "Branches",
          type: "array",
        },
        markdown: {
          description: "Rendered SOP markdown cache.",
          title: "Markdown",
          type: "string",
        },
        name: {
          description: "Stable kebab-case SOP name.",
          title: "Name",
          type: "string",
        },
        parameters: {
          description: "Declared runtime parameters.",
          items: {
            $ref: "#/$defs/SopParam",
          },
          title: "Parameters",
          type: "array",
        },
        steps: {
          description: "Canonical structured SOP steps.",
          items: {
            $ref: "#/$defs/SopStep",
          },
          title: "Steps",
          type: "array",
        },
        summary: {
          description: "Human-readable redacted completion summary.",
          title: "Summary",
          type: "string",
        },
        title: {
          description: "Human-readable SOP title.",
          title: "Title",
          type: "string",
        },
        trajectory: {
          description: "Captured free-roam trajectory frames.",
          items: {
            $ref: "#/$defs/TrajectoryFrame",
          },
          title: "Trajectory",
          type: "array",
        },
      },
      required: ["summary", "markdown", "title", "name", "steps", "parameters"],
      title: "AuthorSopOutput",
      type: "object",
    },
    AuthorSopPause: {
      description:
        "Reason SOP authoring parked for an answer, credential handoff, or temporary operator control.",
      discriminator: {
        mapping: {
          credentials: "#/$defs/CredentialsPause",
          operator_takeover: "#/$defs/OperatorTakeoverPause",
          question: "#/$defs/QuestionPause",
        },
        propertyName: "kind",
      },
      oneOf: [
        {
          $ref: "#/$defs/QuestionPause",
        },
        {
          $ref: "#/$defs/CredentialsPause",
        },
        {
          $ref: "#/$defs/OperatorTakeoverPause",
        },
      ],
    },
    AuthorSopResultArtifact: {
      additionalProperties: false,
      description: "SOP-authoring structured result artifact.",
      properties: {
        data: {
          $ref: "#/$defs/AuthorSopOutput",
          description: "Typed SOP-authoring result.",
        },
        name: {
          const: "result_json",
          description: "Artifact discriminator.",
          title: "Name",
          type: "string",
        },
      },
      required: ["name", "data"],
      title: "AuthorSopResultArtifact",
      type: "object",
    },
    AuthorSopResume: {
      description:
        "Typed directive that answers, annotates, captures, branches, re-phases, or completes a parked SOP-authoring session.",
      discriminator: {
        mapping: {
          annotation: "#/$defs/AnnotationResume",
          answer: "#/$defs/TextResume",
          branch: "#/$defs/BranchResume",
          capture: "#/$defs/CaptureResume",
          done: "#/$defs/DoneResume",
          set_phase: "#/$defs/SetPhaseResume",
          takeover: "#/$defs/TakeoverResume",
        },
        propertyName: "kind",
      },
      oneOf: [
        {
          $ref: "#/$defs/TextResume",
        },
        {
          $ref: "#/$defs/TakeoverResume",
        },
        {
          $ref: "#/$defs/AnnotationResume",
        },
        {
          $ref: "#/$defs/CaptureResume",
        },
        {
          $ref: "#/$defs/BranchResume",
        },
        {
          $ref: "#/$defs/SetPhaseResume",
        },
        {
          $ref: "#/$defs/DoneResume",
        },
      ],
    },
    AuthorSopSignal: {
      description:
        "SOP-authoring action, draft mutation, phase transition, parameter replacement, or advisory liveness signal.",
      discriminator: {
        mapping: {
          heartbeat: "#/$defs/HeartbeatSignal",
          sop_params: "#/$defs/SopParamsSignal",
          sop_phase: "#/$defs/SopPhaseSignal",
          sop_retract: "#/$defs/SopRetractSignal",
          sop_step: "#/$defs/SopStepSignal",
          step: "#/$defs/StepSignal",
        },
        propertyName: "kind",
      },
      oneOf: [
        {
          $ref: "#/$defs/StepSignal",
        },
        {
          $ref: "#/$defs/HeartbeatSignal",
        },
        {
          $ref: "#/$defs/SopStepSignal",
        },
        {
          $ref: "#/$defs/SopRetractSignal",
        },
        {
          $ref: "#/$defs/SopParamsSignal",
        },
        {
          $ref: "#/$defs/SopPhaseSignal",
        },
      ],
    },
    AuthoredTrajectoryPath: {
      additionalProperties: false,
      description: "One alternate path in an authored browser trajectory.",
      properties: {
        branchPoint: {
          $ref: "#/$defs/TrajectoryBranchPoint",
          description: "Fork location and condition.",
        },
        pathId: {
          description: "Durable alternate-path identifier.",
          minLength: 1,
          title: "Pathid",
          type: "string",
        },
      },
      required: ["pathId", "branchPoint"],
      title: "AuthoredTrajectoryPath",
      type: "object",
    },
    BranchCheckKind: {
      description:
        "A branch path's deterministic predicate: ``url_contains`` / ``text_visible`` mirror\n:class:`VerifyKind` (probed against the live page at the branch node); ``param_equals``\ncompares a named run parameter against a literal value — pure, no page probe.",
      enum: ["url_contains", "text_visible", "param_equals"],
      title: "BranchCheckKind",
      type: "string",
    },
    BranchDecision: {
      additionalProperties: false,
      description: "Terminal record of one branch path selected during replay.",
      properties: {
        index: {
          description: "One-based branch position in the run plan.",
          minimum: 1,
          title: "Index",
          type: "integer",
        },
        label: {
          description: "Redacted human-readable path condition.",
          title: "Label",
          type: "string",
        },
        mode: {
          description: "Decision mechanism.",
          enum: ["deterministic", "agentic"],
          title: "Mode",
          type: "string",
        },
        pathId: {
          description: "Chosen durable path id.",
          minLength: 1,
          title: "Pathid",
          type: "string",
        },
        stepId: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Persisted branch step id.",
          title: "Stepid",
        },
      },
      required: ["index", "pathId", "label", "mode"],
      title: "BranchDecision",
      type: "object",
    },
    BranchPathInput: {
      additionalProperties: false,
      description:
        "One labelled alternate of a branch node on the run-plan wire (Node's camelCase\n``paths[].{pathId, stepIds}`` serialized snake_case). ``check`` ``None`` marks the default\npath (evaluated last); ``step_ids`` are the member action steps replay skips when this path\nisn't chosen.",
      properties: {
        check: {
          anyOf: [
            {
              $ref: "#/$defs/ai_service__a2a__run_sop__config__models__BranchCheck",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Deterministic selection predicate; null marks the default path evaluated after checked alternatives.",
        },
        label: {
          default: "",
          description:
            "Human-readable condition or outcome represented by the path.",
          title: "Label",
          type: "string",
        },
        pathId: {
          default: "",
          description: "Durable identifier of this alternate branch path.",
          title: "Pathid",
          type: "string",
        },
        stepIds: {
          default: [],
          description:
            "Persisted action-step ids that belong to this path and are skipped when another path is selected.",
          items: {
            type: "string",
          },
          title: "Stepids",
          type: "array",
        },
      },
      title: "BranchPathInput",
      type: "object",
    },
    BranchResume: {
      additionalProperties: false,
      description: "Begin capturing an alternate SOP path.",
      properties: {
        conditionLabel: {
          description:
            "Human-readable condition selecting this alternate path.",
          minLength: 1,
          title: "Conditionlabel",
          type: "string",
        },
        fromFrameIndex: {
          anyOf: [
            {
              minimum: 0,
              type: "integer",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Optional zero-based trajectory frame at which the path forks.",
          title: "Fromframeindex",
        },
        kind: {
          const: "branch",
          description: "Resume-directive discriminator.",
          title: "Kind",
          type: "string",
        },
        parameters: {
          description: "Complete current SOP parameter declarations.",
          items: {
            $ref: "#/$defs/SopParam",
          },
          title: "Parameters",
          type: "array",
        },
      },
      required: ["kind", "conditionLabel"],
      title: "BranchResume",
      type: "object",
    },
    BriefConfig: {
      additionalProperties: false,
      description:
        "Optional seed for the SOP being authored. Both fields may be left unset — the\ninterview elicits a title + objective from the user when they are missing (discovery\nmode), then surfaces them for inline editing.",
      properties: {
        objective: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Optional initial statement of the outcome the SOP must achieve; null asks the interview to elicit it.",
          title: "Objective",
        },
        title: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Optional initial SOP title; null asks the interview to discover and confirm one with the user.",
          title: "Title",
        },
      },
      title: "BriefConfig",
      type: "object",
    },
    BrowserConfig: {
      additionalProperties: false,
      description: "Operator-specific; the browser environment Node provides.",
      properties: {
        cdpUrl: {
          description:
            "Chrome DevTools Protocol URL for the caller-owned browser session the operator attaches to.",
          title: "Cdpurl",
          type: "string",
        },
        displayHeight: {
          default: 720,
          description:
            "Browser viewport height in pixels, shared with the computer-use model so generated coordinates match the caller's display.",
          minimum: 1,
          title: "Displayheight",
          type: "integer",
        },
        displayWidth: {
          default: 1280,
          description:
            "Browser viewport width in pixels, shared with the computer-use model so generated coordinates match the caller's display.",
          minimum: 1,
          title: "Displaywidth",
          type: "integer",
        },
        startUrl: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Optional page URL the operator opens before its first model turn; null preserves the browser's current page.",
          title: "Starturl",
        },
      },
      required: ["cdpUrl"],
      title: "BrowserConfig",
      type: "object",
    },
    BrowserUseArtifact: {
      description:
        "Human-readable or structured terminal browser-use deliverable.",
      discriminator: {
        mapping: {
          result: "#/$defs/ResultTextArtifact",
          result_json: "#/$defs/BrowserUseResultArtifact",
        },
        propertyName: "name",
      },
      oneOf: [
        {
          $ref: "#/$defs/ResultTextArtifact",
        },
        {
          $ref: "#/$defs/BrowserUseResultArtifact",
        },
      ],
    },
    BrowserUseInput: {
      additionalProperties: false,
      description:
        "Initial configuration for the autonomous browser-use skill.",
      properties: {
        agent: {
          $ref: "#/$defs/OperatorAgentConfig",
          description:
            "Model selection, output contract, and execution safety bounds.",
        },
        expectDownloads: {
          default: false,
          description:
            "The step expects the run to download a file. After the Driver reports done, the run waits (bounded) until at least one download has completed, including one that has not begun yet, and fails with 'No download arrived' if none does.",
          title: "Expectdownloads",
          type: "boolean",
        },
        knownValues: {
          description:
            "The step's connected data inputs, by key, label and value. The experimental Jev Driver types only these; other Drivers read them through the Instructions.",
          items: {
            $ref: "#/$defs/KnownValue",
          },
          title: "Knownvalues",
          type: "array",
        },
        procedures: {
          description:
            "Pinned, parameter-resolved procedures the operator is allowed to read or execute during this run.",
          items: {
            $ref: "#/$defs/ProcedureInput",
          },
          title: "Procedures",
          type: "array",
        },
        session: {
          $ref: "#/$defs/SessionConfig",
          description:
            "Caller-owned browser session and secure input environment.",
        },
        sessionRules: {
          description:
            "Active SOP rules for the entire logical task, including retries and recovery.",
          items: {
            maxLength: 4000,
            minLength: 1,
            pattern:
              "^[^\\u0000-\\u001f\\u007f-\\u009f\\u2028\\u2029]*[^\\u0000-\\u001f\\u007f-\\u009f\\u2028\\u2029\\u0009-\\u000d\\u001c-\\u0020\\u0085\\u00a0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000][^\\u0000-\\u001f\\u007f-\\u009f\\u2028\\u2029]*$",
            type: "string",
          },
          title: "Sessionrules",
          type: "array",
        },
      },
      required: ["session", "agent"],
      title: "BrowserUseInput",
      type: "object",
    },
    BrowserUseOutput: {
      additionalProperties: false,
      description:
        "Structured browser-use terminal result with collision-safe protocol fields.",
      properties: {
        output: {
          anyOf: [
            {
              $ref: "#/$defs/JsonValue",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Caller-defined structured output, validated separately by its schema.",
        },
        procedureRuns: {
          description:
            "Inline procedure replays performed during the turn, in run order.",
          items: {
            $ref: "#/$defs/ProcedureRun",
          },
          title: "Procedureruns",
          type: "array",
        },
        repairs: {
          description: "Repairs produced by inline procedure replay.",
          items: {
            $ref: "#/$defs/Repair",
          },
          title: "Repairs",
          type: "array",
        },
        summary: {
          description: "Human-readable redacted run summary.",
          title: "Summary",
          type: "string",
        },
      },
      required: ["summary"],
      title: "BrowserUseOutput",
      type: "object",
    },
    BrowserUsePause: {
      description:
        "Reason browser use parked for a human answer or temporary operator control.",
      discriminator: {
        mapping: {
          operator_takeover: "#/$defs/OperatorTakeoverPause",
          question: "#/$defs/QuestionPause",
        },
        propertyName: "kind",
      },
      oneOf: [
        {
          $ref: "#/$defs/QuestionPause",
        },
        {
          $ref: "#/$defs/OperatorTakeoverPause",
        },
      ],
    },
    BrowserUseResultArtifact: {
      additionalProperties: false,
      description: "Browser-use structured result artifact.",
      properties: {
        data: {
          $ref: "#/$defs/BrowserUseOutput",
          description: "Typed browser-use result envelope.",
        },
        name: {
          const: "result_json",
          description: "Artifact discriminator.",
          title: "Name",
          type: "string",
        },
      },
      required: ["name", "data"],
      title: "BrowserUseResultArtifact",
      type: "object",
    },
    BrowserUseResume: {
      description:
        "Human response that resumes browser use after either a model question or a temporary operator takeover.",
      discriminator: {
        mapping: {
          answer: "#/$defs/TextResume",
          takeover: "#/$defs/TakeoverResume",
        },
        propertyName: "kind",
      },
      oneOf: [
        {
          $ref: "#/$defs/TextResume",
        },
        {
          $ref: "#/$defs/TakeoverResume",
        },
      ],
    },
    BrowserUseSignal: {
      description:
        "Browser-use action progress, inline procedure-replay step lifecycle and branch decision, or advisory liveness signal.",
      discriminator: {
        mapping: {
          heartbeat: "#/$defs/HeartbeatSignal",
          sop_replay_branch: "#/$defs/ReplayBranchDecision",
          sop_replay_step: "#/$defs/ReplayStepCompleted",
          sop_replay_step_started: "#/$defs/ReplayStepStarted",
          step: "#/$defs/StepSignal",
        },
        propertyName: "kind",
      },
      oneOf: [
        {
          $ref: "#/$defs/StepSignal",
        },
        {
          $ref: "#/$defs/HeartbeatSignal",
        },
        {
          $ref: "#/$defs/ReplayStepStarted",
        },
        {
          $ref: "#/$defs/ReplayStepCompleted",
        },
        {
          $ref: "#/$defs/ReplayBranchDecision",
        },
      ],
    },
    CaptureResume: {
      additionalProperties: false,
      description:
        "Capture the human's newly demonstrated actions as an SOP step.",
      properties: {
        kind: {
          const: "capture",
          description: "Resume-directive discriminator.",
          title: "Kind",
          type: "string",
        },
        parameters: {
          description: "Complete current SOP parameter declarations.",
          items: {
            $ref: "#/$defs/SopParam",
          },
          title: "Parameters",
          type: "array",
        },
      },
      required: ["kind"],
      title: "CaptureResume",
      type: "object",
    },
    CodeKind: {
      description:
        "Durability category of captured replay code: semantic reference or coordinates.",
      enum: ["ref", "coordinate"],
      title: "CodeKind",
      type: "string",
    },
    CodeRuntimeKind: {
      description: "Isolation environment in which captured SOP code executes.",
      enum: ["inprocess", "daytona"],
      title: "CodeRuntimeKind",
      type: "string",
    },
    ConfirmActionPause: {
      additionalProperties: false,
      description:
        "The human must perform an action on an interactive live preview.",
      properties: {
        action: {
          description: "Action the human must perform.",
          minLength: 1,
          title: "Action",
          type: "string",
        },
        kind: {
          const: "confirm_action",
          description: "Pause discriminator.",
          title: "Kind",
          type: "string",
        },
        liveViewUrl: {
          description:
            "Interactive live-view URL on which to perform the action.",
          minLength: 1,
          title: "Liveviewurl",
          type: "string",
        },
      },
      required: ["kind", "action", "liveViewUrl"],
      title: "ConfirmActionPause",
      type: "object",
    },
    ConfirmBrowserConfig: {
      additionalProperties: false,
      description:
        "Optional interactive Kernel browser for the per-screen confirm loop (AI-340).\n\nPresent only when Node provisioned an interactive (``headless:false``) Kernel browser for this\nrun — its CDP url lets the ai-service navigate/install the gesture recorder/drain, and its\n``live_view_url`` is what the studio surfaces so the user can drive the mock during a\n``confirm_action`` pause. Absent ⇒ ``confirm_action`` degrades to a text-only review pause\nagainst the passive Daytona preview (no automated gesture/`__evalState` feedback).",
      properties: {
        cdpUrl: {
          description:
            "Chrome DevTools Protocol URL for the caller-owned interactive confirmation browser.",
          minLength: 1,
          title: "Cdpurl",
          type: "string",
        },
        liveViewUrl: {
          description:
            "Interactive browser view surfaced while the user performs a confirmation action.",
          minLength: 1,
          title: "Liveviewurl",
          type: "string",
        },
      },
      required: ["cdpUrl", "liveViewUrl"],
      title: "ConfirmBrowserConfig",
      type: "object",
    },
    ContractMetadata: {
      additionalProperties: false,
      description:
        "Application-contract metadata placed on every outbound A2A message.",
      properties: {
        agentRunId: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Node's AgentRun.id for this run, letting ai-service logs and traces be cross-referenced back to the originating Node row.",
          title: "Agentrunid",
        },
        contractRevision: {
          description:
            "SHA-256 revision of the canonical v1 schema, skill registry metadata, and schema-root mappings.",
          maxLength: 64,
          minLength: 64,
          pattern: "^[a-f0-9]{64}$",
          title: "Contractrevision",
          type: "string",
        },
        contractVersion: {
          const: 1,
          default: 1,
          description: "Major application-contract version.",
          title: "Contractversion",
          type: "integer",
        },
        kernelSessionId: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Caller-owned browser session id used for display-level control and cross-service trace correlation.",
          title: "Kernelsessionid",
        },
        organizationId: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Organization that owns and is billed for this run, mirroring Node's AgentRun.organizationId. Used to attribute AI spend.",
          title: "Organizationid",
        },
        skillId: {
          $ref: "#/$defs/SkillId",
          description: "Skill routing identifier.",
        },
        spendTags: {
          anyOf: [
            {
              $ref: "#/$defs/AiAttributionTags",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Bounded-cardinality dimensions emitted as LiteLLM spend tags. The run ids above are deliberately NOT tags — they are unbounded per day and would make the proxy's daily aggregates unusable — so they stay here for logs and traces while this block carries the definition-level ids that can be aggregated. Nested rather than flattened so the tag vocabulary has exactly one shape, shared with the header envelope used by the REST routes.",
        },
        stepRunId: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Node's StepRun.id for the workflow step that triggered this run, when applicable, else null. Used to attribute AI spend to a specific workflow step.",
          title: "Steprunid",
        },
        traceparent: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Optional W3C trace-context parent.",
          title: "Traceparent",
        },
        tracestate: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Optional W3C trace-context vendor state.",
          title: "Tracestate",
        },
        userId: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "End user who triggered this run, mirroring Node's AgentRun.triggeredBy. Null for system- or schedule-initiated runs. Used to attribute AI spend.",
          title: "Userid",
        },
        workflowRunId: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Node's WorkflowRun.id when this run was triggered by the DAG workflow engine, else null. Used to attribute AI spend to a specific workflow execution.",
          title: "Workflowrunid",
        },
      },
      required: ["skillId", "contractRevision"],
      title: "ContractMetadata",
      type: "object",
    },
    CredentialFill: {
      additionalProperties: false,
      description:
        "Value-free descriptor for a ``credential_fill`` step: fill the secret named\n``credential_name`` into ``selector``, then optionally submit. The secret VALUE never lives\nhere — the surface resolves it from the credential map (or, for the reserved dynamic name, a\nlive supplier) at replay and types it straight into the DOM, so it never enters stored code,\nthe sandbox, or a screenshot (secrecy invariant §8).\n\n``submit`` presses Enter after the fill. ``submit_selector`` (optional) is clicked instead when\nvisible — preserving a login's exact submit-button safety (e.g. the RDP dialog's\n``:text-is(\"OK\")``, which must not match page chrome). ``visible_timeout_ms`` bounds how long\nthe surface waits for ``selector`` before treating the field as absent (a ``best_effort`` step\nthen no-ops; a required one fails).\n\n**An EMPTY ``selector`` is the in-desktop (canvas) variant**: the secret is typed by keyboard\ninto whatever element currently has focus, because an app rendered inside a streamed remote\ndesktop (Famous in AVD) has no DOM field to address — the page is the client chrome around a\nvideo canvas. The step before it must put the cursor in the field (an ordinary coordinate\nclick step, which hardens and self-heals like any other). This variant is honored ONLY on a\nvisual replay; on a DOM page an empty selector is a configuration error rather than a blind\ntype, so a web SOP can never silently fill the wrong element. It also cannot detect an absent\nfield — see :meth:`InterviewSurface.fill_credential_focused`.",
      properties: {
        credentialName: {
          description:
            "Non-secret credential key resolved at replay time; never the secret value itself.",
          title: "Credentialname",
          type: "string",
        },
        selector: {
          default: "",
          description:
            "CSS selector of the field into which the secret is typed; empty types into the focused element for an in-desktop (canvas) fill.",
          title: "Selector",
          type: "string",
        },
        submit: {
          default: false,
          description: "Whether to press Enter after filling the credential.",
          title: "Submit",
          type: "boolean",
        },
        submitSelector: {
          default: "",
          description:
            "Optional precise selector clicked instead of pressing Enter when it is visible.",
          title: "Submitselector",
          type: "string",
        },
        visibleTimeoutMs: {
          default: 5000,
          description:
            "Maximum milliseconds to wait for the credential field before treating it as absent.",
          title: "Visibletimeoutms",
          type: "integer",
        },
      },
      required: ["credentialName"],
      title: "CredentialFill",
      type: "object",
    },
    CredentialRejectedError: {
      additionalProperties: false,
      description:
        "A stored credential was POSITIVELY OBSERVED to be rejected by the system it was\npresented to, so the run cannot proceed until a human fixes the credential.\n\nReported only on positive evidence — the sign-in page showed its own error, or the\noperator exhausted ``fill_credential``'s per-field cap and then ended the task as failed.\nA timeout, a desktop canvas that never appeared, a dropped session, or any other\ninfrastructure failure stays ``execution_failed``/``internal``: a bad credential and a bad\nmorning at Azure must never look alike to the consumer, which pauses the credential on this\ncode.\n\n``surface`` says where the rejection happened:\n\n- ``portal`` — the Azure/Entra sign-in page driven by the pre-agent AVD login.\n- ``vm`` — the in-session RDP password prompt. RESERVED: nothing emits it yet (the client\n  paints that prompt into the canvas bitmap and no proven selector reads it), but the value\n  is in the vocabulary so adding the detector is not a wire-breaking change.\n- ``application`` — an application inside the desktop (Famous, …), reached via the\n  operator's ``fill_credential`` tool.\n\nOnly non-secret identifiers cross the wire: the credential's label and its id as Node knows\nit, never a username or password value.",
      properties: {
        code: {
          const: "credential_rejected",
          description: "Error discriminator.",
          title: "Code",
          type: "string",
        },
        credentialId: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "The rejected credential's id as Node knows it (vault/credential-set UUID), or null when the producer could not resolve one.",
          title: "Credentialid",
        },
        credentialName: {
          description:
            "Non-secret label of the rejected credential. Never a secret value.",
          maxLength: 200,
          minLength: 1,
          pattern:
            "[^\\u0009-\\u000d\\u001c-\\u0020\\u0085\\u00a0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000]",
          title: "Credentialname",
          type: "string",
        },
        message: {
          anyOf: [
            {
              maxLength: 300,
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Redacted detail.",
          title: "Message",
        },
        retryable: {
          const: false,
          default: false,
          description: "Retry policy.",
          title: "Retryable",
          type: "boolean",
        },
        surface: {
          description: "Where the credential was presented and rejected.",
          enum: ["portal", "vm", "application"],
          title: "Surface",
          type: "string",
        },
      },
      required: ["code", "credentialName", "surface"],
      title: "CredentialRejectedError",
      type: "object",
    },
    CredentialsConfig: {
      additionalProperties: false,
      description:
        "By-reference only — credential *values* never cross the wire (spec §13).\n\n``organization_id`` travels alongside ``credential_set_id`` so the production\nresolver (AI-246) can call Node's org-scoped resolution endpoint — the credential\ntable itself is organization-scoped, so there is no resolving a\n``credential_set_id`` without knowing which org it belongs to.",
      properties: {
        applicationCredentials: {
          description:
            "Named in-desktop application credential references exposed through the secure fill-credential tool.",
          items: {
            $ref: "#/$defs/ApplicationCredentialRef",
          },
          title: "Applicationcredentials",
          type: "array",
        },
        autoLogin: {
          default: false,
          description:
            "Whether the operator should attempt its supported automatic browser login flow before beginning the requested task.",
          title: "Autologin",
          type: "boolean",
        },
        credentialSetId: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Opaque reference to the primary browser-login credential set; null means no primary credentials are available.",
          title: "Credentialsetid",
        },
        organizationId: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Organization that owns the referenced credentials and scopes every server-side resolution request.",
          title: "Organizationid",
        },
        otpTargetId: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Opaque OTP target reference used to resolve an in-browser MFA challenge via the fill_otp_code tool; null means no MFA tool is offered.",
          title: "Otptargetid",
        },
      },
      title: "CredentialsConfig",
      type: "object",
    },
    CredentialsPause: {
      additionalProperties: false,
      description: "The skill needs a credential reference for one domain.",
      properties: {
        domain: {
          description: "Site or domain requiring credentials.",
          minLength: 1,
          title: "Domain",
          type: "string",
        },
        kind: {
          const: "credentials",
          description: "Pause discriminator.",
          title: "Kind",
          type: "string",
        },
      },
      required: ["kind", "domain"],
      title: "CredentialsPause",
      type: "object",
    },
    DiffArtifact: {
      additionalProperties: false,
      description: "The approved mock's textual source diff.",
      properties: {
        name: {
          const: "diff",
          description: "Artifact discriminator.",
          title: "Name",
          type: "string",
        },
        text: {
          description: "Unified diff text.",
          title: "Text",
          type: "string",
        },
      },
      required: ["name", "text"],
      title: "DiffArtifact",
      type: "object",
    },
    DoneResume: {
      additionalProperties: false,
      description:
        "Finalize a parked free-roam SOP capture without another model turn.",
      properties: {
        kind: {
          const: "done",
          description: "Resume-directive discriminator.",
          title: "Kind",
          type: "string",
        },
        parameters: {
          description: "Complete final SOP parameter declarations.",
          items: {
            $ref: "#/$defs/SopParam",
          },
          title: "Parameters",
          type: "array",
        },
      },
      required: ["kind"],
      title: "DoneResume",
      type: "object",
    },
    EvalPathCase: {
      additionalProperties: false,
      description:
        "One executable `?case=` scenario projected from a mock state graph.",
      properties: {
        expect: {
          $ref: "#/$defs/JsonObject",
          description: "Caller-defined expected terminal observable values.",
        },
        id: {
          description: "Stable evaluation-case id.",
          minLength: 1,
          title: "Id",
          type: "string",
        },
        params: {
          additionalProperties: {
            type: "string",
          },
          description: "Concrete runtime parameter bindings.",
          title: "Params",
          type: "object",
        },
        path: {
          description: "Ordered actions in the case.",
          items: {
            type: "string",
          },
          title: "Path",
          type: "array",
        },
        shots: {
          description: "Screenshot references aligned with the path actions.",
          items: {
            type: "string",
          },
          title: "Shots",
          type: "array",
        },
        successCriteria: {
          default: "",
          description: "Plain-English pass condition.",
          title: "Successcriteria",
          type: "string",
        },
        task: {
          description: "Natural-language task for this case.",
          title: "Task",
          type: "string",
        },
      },
      required: ["id", "task", "path"],
      title: "EvalPathCase",
      type: "object",
    },
    EvalPaths: {
      additionalProperties: false,
      description: "Evaluation-case forest emitted alongside the state graph.",
      properties: {
        cases: {
          description: "Executable cases.",
          items: {
            $ref: "#/$defs/EvalPathCase",
          },
          title: "Cases",
          type: "array",
        },
        changelog: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Human-readable summary of the approved changes.",
          title: "Changelog",
        },
        hostedUrl: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Durable published mock URL when available.",
          title: "Hostedurl",
        },
        parameters: {
          description: "Runtime parameter declarations.",
          items: {
            $ref: "#/$defs/GraphParameter",
          },
          title: "Parameters",
          type: "array",
        },
        screens: {
          description: "Storyboard screen labels.",
          items: {
            type: "string",
          },
          title: "Screens",
          type: "array",
        },
        site: {
          default: "",
          description: "Stable mock site slug.",
          title: "Site",
          type: "string",
        },
      },
      title: "EvalPaths",
      type: "object",
    },
    ExecutionConfig: {
      additionalProperties: false,
      description: "Platform-shared: loop bounds.",
      properties: {
        maxIterations: {
          default: 50,
          description:
            "Maximum agent-loop iterations permitted in one turn before the run is stopped.",
          maximum: 200,
          minimum: 1,
          title: "Maxiterations",
          type: "integer",
        },
        timeoutSeconds: {
          default: 300,
          description:
            "Wall-clock timeout in seconds for one agent turn; a resumed turn receives a fresh budget.",
          maximum: 7200,
          minimum: 10,
          title: "Timeoutseconds",
          type: "integer",
        },
      },
      title: "ExecutionConfig",
      type: "object",
    },
    ExecutionFailedError: {
      additionalProperties: false,
      description:
        "A skill completed normally but could not satisfy its requested outcome.",
      properties: {
        code: {
          const: "execution_failed",
          description: "Error discriminator.",
          title: "Code",
          type: "string",
        },
        message: {
          anyOf: [
            {
              maxLength: 300,
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Redacted detail.",
          title: "Message",
        },
        retryable: {
          const: false,
          default: false,
          description: "Retry policy.",
          title: "Retryable",
          type: "boolean",
        },
      },
      required: ["code"],
      title: "ExecutionFailedError",
      type: "object",
    },
    FileArtifact: {
      additionalProperties: false,
      description: "One edited text file produced by the mock studio.",
      properties: {
        name: {
          description: "Artifact name in the form `file:<mock-relative path>`.",
          pattern: "^file:.+",
          title: "Name",
          type: "string",
        },
        text: {
          description: "Complete redacted file contents.",
          title: "Text",
          type: "string",
        },
      },
      required: ["name", "text"],
      title: "FileArtifact",
      type: "object",
    },
    GeminiModelConfig: {
      additionalProperties: false,
      description: "Gemini computer-use model selection and generation limits.",
      properties: {
        maxTokens: {
          default: 16384,
          description:
            "Maximum output-token budget for each computer-use model turn.",
          minimum: 1,
          title: "Maxtokens",
          type: "integer",
        },
        modelId: {
          description: "Allow-listed Gemini computer-use model identifier.",
          enum: [
            "gemini-3.5-flash",
            "gemini-3.6-flash",
            "gemini-3.7-flash",
            "gemini-3.8-flash",
          ],
          title: "Modelid",
          type: "string",
        },
        provider: {
          const: "gemini",
          default: "gemini",
          description: "Provider discriminator selecting the Gemini driver.",
          title: "Provider",
          type: "string",
        },
        thinkingBudget: {
          default: 1024,
          description:
            "Requested extended-thinking token budget; currently consumed by Anthropic and ignored by providers without an equivalent control.",
          minimum: 0,
          title: "Thinkingbudget",
          type: "integer",
        },
      },
      required: ["modelId"],
      title: "GeminiModelConfig",
      type: "object",
    },
    GraphNodeAnchor: {
      additionalProperties: false,
      description: "Deep-link coordinates for one captured mock state.",
      properties: {
        case: {
          default: "",
          description: "Evaluation-case id.",
          title: "Case",
          type: "string",
        },
        step: {
          default: -1,
          description:
            "Zero-based action index, with -1 denoting the root screen.",
          minimum: -1,
          title: "Step",
          type: "integer",
        },
      },
      title: "GraphNodeAnchor",
      type: "object",
    },
    GraphParameter: {
      additionalProperties: false,
      description: "One runtime parameter declared by a mock's state contract.",
      properties: {
        default: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Optional default binding.",
          title: "Default",
        },
        description: {
          default: "",
          description: "Human-readable parameter purpose.",
          title: "Description",
          type: "string",
        },
        name: {
          description: "Stable snake-case parameter name.",
          pattern: "^[a-z0-9_]+$",
          title: "Name",
          type: "string",
        },
        required: {
          default: true,
          description: "Whether callers must bind the parameter.",
          title: "Required",
          type: "boolean",
        },
      },
      required: ["name"],
      title: "GraphParameter",
      type: "object",
    },
    GraphPath: {
      additionalProperties: false,
      description:
        "One root-to-terminal evaluation path through a mock state graph.",
      properties: {
        edgeIds: {
          description: "Traversed graph edge ids.",
          items: {
            type: "string",
          },
          title: "Edgeids",
          type: "array",
        },
        id: {
          description: "Evaluation-case id.",
          minLength: 1,
          title: "Id",
          type: "string",
        },
        nodeIds: {
          description: "Visited graph node ids.",
          items: {
            type: "string",
          },
          title: "Nodeids",
          type: "array",
        },
        params: {
          additionalProperties: {
            type: "string",
          },
          description: "Concrete runtime parameter bindings for this path.",
          title: "Params",
          type: "object",
        },
        successCriteria: {
          default: "",
          description: "Plain-English condition for a successful replay.",
          title: "Successcriteria",
          type: "string",
        },
        task: {
          default: "",
          description: "Natural-language task for the path.",
          title: "Task",
          type: "string",
        },
      },
      required: ["id"],
      title: "GraphPath",
      type: "object",
    },
    GraphStateEdge: {
      additionalProperties: false,
      description: "One user action that moves the mock between two states.",
      properties: {
        action: {
          default: "",
          description: "Human-readable action.",
          title: "Action",
          type: "string",
        },
        actionKind: {
          default: "",
          description: "Optional normalized action kind.",
          title: "Actionkind",
          type: "string",
        },
        caseIds: {
          description: "Evaluation cases that traverse this edge.",
          items: {
            type: "string",
          },
          title: "Caseids",
          type: "array",
        },
        id: {
          description: "Stable graph edge id.",
          minLength: 1,
          title: "Id",
          type: "string",
        },
        selector: {
          default: "",
          description: "Optional element selector hint.",
          title: "Selector",
          type: "string",
        },
        source: {
          description: "Source node id.",
          minLength: 1,
          title: "Source",
          type: "string",
        },
        target: {
          description: "Target node id.",
          minLength: 1,
          title: "Target",
          type: "string",
        },
      },
      required: ["id", "source", "target"],
      title: "GraphStateEdge",
      type: "object",
    },
    GraphStateNode: {
      additionalProperties: false,
      description: "One observable state in a generated mock UI.",
      properties: {
        anchor: {
          $ref: "#/$defs/GraphNodeAnchor",
          description: "Case and step used to deep-link to this state.",
        },
        evalStateSnapshot: {
          $ref: "#/$defs/JsonObject",
          description: "Captured `window.__evalState` observation.",
        },
        expect: {
          $ref: "#/$defs/JsonObject",
          description:
            "Caller-defined expected observable values at this state.",
        },
        id: {
          description: "Stable graph node id.",
          minLength: 1,
          title: "Id",
          type: "string",
        },
        isRoot: {
          default: false,
          description: "Whether this is the graph root.",
          title: "Isroot",
          type: "boolean",
        },
        isTerminal: {
          default: false,
          description: "Whether this state ends a path.",
          title: "Isterminal",
          type: "boolean",
        },
        label: {
          default: "",
          description: "Human-readable state label.",
          title: "Label",
          type: "string",
        },
        precondition: {
          default: "",
          description: "Condition that must hold in this state.",
          title: "Precondition",
          type: "string",
        },
        prohibitions: {
          description: "Behaviors forbidden while this state is active.",
          items: {
            type: "string",
          },
          title: "Prohibitions",
          type: "array",
        },
        screen: {
          default: "",
          description: "Source storyboard screen.",
          title: "Screen",
          type: "string",
        },
        screenshot: {
          default: "",
          description: "Captured screenshot reference.",
          title: "Screenshot",
          type: "string",
        },
      },
      required: ["id"],
      title: "GraphStateNode",
      type: "object",
    },
    GuideAnnotationInput: {
      additionalProperties: false,
      description:
        "One mark the author drew on a guide frame — the wire mirror of Node's\n``GuideAnnotationSchema``. ``intent`` is declared by the author, never inferred from pixels.",
      properties: {
        id: {
          description: "Stable annotation id within its frame.",
          title: "Id",
          type: "string",
        },
        intent: {
          description:
            "What the mark means: where the author acted, what they read, a control to avoid, or commentary.",
          enum: ["act_here", "read", "avoid", "note"],
          title: "Intent",
          type: "string",
        },
        kind: {
          default: "freehand",
          description:
            "Shape of the mark; decides how `points` are interpreted.",
          enum: ["freehand", "circle", "arrow", "rect", "marker", "text"],
          title: "Kind",
          type: "string",
        },
        label: {
          default: "",
          description:
            "Short label spoken to the model, e.g. the control's name.",
          title: "Label",
          type: "string",
        },
        points: {
          description:
            "Image-normalized vertices: the path (freehand), [from, to] (arrow/rect), one point (marker/text), or [centre, edge] (circle).",
          items: {
            $ref: "#/$defs/GuidePoint",
          },
          minItems: 1,
          title: "Points",
          type: "array",
        },
      },
      required: ["id", "intent", "points"],
      title: "GuideAnnotationInput",
      type: "object",
    },
    GuideFrameInput: {
      additionalProperties: false,
      description:
        "One visual-guide frame descriptor. The image itself rides the same message as a FilePart\nwhose ``filename`` equals ``frame_id`` — correlation is by name, never by position, so one\nundecodable part cannot shift every later frame.",
      properties: {
        annotations: {
          default: [],
          description:
            "Marks drawn on the frame; already rasterized into the FilePart image.",
          items: {
            $ref: "#/$defs/GuideAnnotationInput",
          },
          title: "Annotations",
          type: "array",
        },
        frameId: {
          description: "Frame id; also the filename of its image FilePart.",
          title: "Frameid",
          type: "string",
        },
        label: {
          default: "",
          description: "Author's label for the screen.",
          title: "Label",
          type: "string",
        },
        mode: {
          anyOf: [
            {
              enum: ["on_heal", "always"],
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "This owning version's guide mode; null falls back to the enclosing guide mode for legacy payloads.",
          title: "Mode",
        },
        ordinal: {
          description: "1-based position in the guide.",
          minimum: 1,
          title: "Ordinal",
          type: "integer",
        },
        ownerVersionId: {
          default: "",
          description:
            "SOP version that owns this frame and its step ids; empty only for legacy payloads.",
          title: "Ownerversionid",
          type: "string",
        },
        screenType: {
          default: "",
          description:
            "Optional screen role marker (`login`, `file-upload`, `file-download`).",
          title: "Screentype",
          type: "string",
        },
        stepIds: {
          default: [],
          description:
            "Steps this frame shows — the screen as it looked BEFORE acting on them.",
          items: {
            type: "string",
          },
          title: "Stepids",
          type: "array",
        },
      },
      required: ["frameId", "ordinal"],
      title: "GuideFrameInput",
      type: "object",
    },
    GuideInput: {
      additionalProperties: false,
      description:
        "The run's visual guide (AI-488 follow-on): reference frames shown to the agentic\ncomponents as in-context exemplars. A composed run may aggregate frames from several owning\nversions; each frame carries its owner and mode. Absent when no represented version contributes\nan enabled frame.",
      properties: {
        frames: {
          description:
            "Frame descriptors, index-independent of the message's FileParts.",
          items: {
            $ref: "#/$defs/GuideFrameInput",
          },
          minItems: 1,
          title: "Frames",
          type: "array",
          "x-uniqueProperties": ["frameId", "ordinal"],
        },
        mode: {
          description:
            "`on_heal`: frames only when a recorded step failed and is re-performed; `always`: also for spec-only steps performed agentically.",
          enum: ["on_heal", "always"],
          title: "Mode",
          type: "string",
        },
      },
      required: ["mode", "frames"],
      title: "GuideInput",
      type: "object",
    },
    GuidePoint: {
      additionalProperties: false,
      description:
        "One annotation vertex, normalized to the frame's base image (0..1 on both axes).",
      properties: {
        x: {
          description: "Horizontal position as a fraction of width.",
          maximum: 1,
          minimum: 0,
          title: "X",
          type: "number",
        },
        y: {
          description: "Vertical position as a fraction of height.",
          maximum: 1,
          minimum: 0,
          title: "Y",
          type: "number",
        },
      },
      required: ["x", "y"],
      title: "GuidePoint",
      type: "object",
    },
    HealMode: {
      description:
        "How a drifted replay step is recovered. ``semantic`` (default, today's behavior) heals only\n*ref* steps over the a11y surface and hard-stops on a coordinate step. ``auto`` additionally\ngives a failed *coordinate* step one bounded computer-use heal (``ComputerHealer``) instead of\nstopping; ref/code-less steps still heal semantically. ``off`` replays only: no healer and no\nbranch chooser is built, so the run never calls a model and a failing step fails the run.",
      enum: ["semantic", "auto", "off"],
      title: "HealMode",
      type: "string",
    },
    HeartbeatSignal: {
      additionalProperties: false,
      description:
        "Advisory liveness signal emitted while useful work is otherwise silent.",
      properties: {
        idleSeconds: {
          description:
            "Seconds since the last content-bearing progress message.",
          minimum: 0,
          title: "Idleseconds",
          type: "number",
        },
        kind: {
          const: "heartbeat",
          description: "Signal discriminator.",
          title: "Kind",
          type: "string",
        },
      },
      required: ["kind", "idleSeconds"],
      title: "HeartbeatSignal",
      type: "object",
    },
    InputConfig: {
      additionalProperties: false,
      description:
        "Input pacing for the Kernel display layer. Defaults are ROBOTIC (instant) —\nraise them to add human-like anti-bot realism.\n\nKernel's ``click_mouse`` has no smoothing flag: it glides the cursor from its last\nposition to the target server-side. Since we no-op the model's ``mouse_move`` steps\nby default, that glide is what shows up as \"human-like\" drift on clicks.\n``snap_cursor_before_click`` (default True) teleports the cursor onto the target\nfirst so the click travels zero distance — set it False to let clicks glide.",
      properties: {
        mouseDurationMs: {
          anyOf: [
            {
              maximum: 5000,
              minimum: 0,
              type: "integer",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Optional duration in milliseconds for a smooth mouse move; null lets Kernel derive the duration from travel distance.",
          title: "Mousedurationms",
        },
        mouseSmooth: {
          default: false,
          description:
            "Whether explicit mouse-move actions follow a human-like Bezier path instead of being dropped or applied immediately.",
          title: "Mousesmooth",
          type: "boolean",
        },
        scrollPixelsPerUnit: {
          default: 100,
          description:
            "Distance in pixels that one unit of a model scroll action travels; lower values scroll in finer increments.",
          maximum: 1000,
          minimum: 1,
          title: "Scrollpixelsperunit",
          type: "integer",
        },
        snapCursorBeforeClick: {
          default: true,
          description:
            "Whether to teleport the cursor to a click target first; false allows Kernel's click operation to visibly glide from the previous position.",
          title: "Snapcursorbeforeclick",
          type: "boolean",
        },
        typingDelayMs: {
          anyOf: [
            {
              maximum: 1000,
              minimum: 0,
              type: "integer",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Delay in milliseconds between synthesized keystrokes; zero types immediately and larger values add human-like pacing. When unset, browser runs type instantly and remote-desktop runs default to 100 ms — instant keystroke injection into a streamed desktop drops and reorders characters.",
          title: "Typingdelayms",
        },
      },
      title: "InputConfig",
      type: "object",
    },
    InternalError: {
      additionalProperties: false,
      description: "An unexpected service failure occurred.",
      properties: {
        code: {
          const: "internal",
          description: "Error discriminator.",
          title: "Code",
          type: "string",
        },
        message: {
          anyOf: [
            {
              maxLength: 300,
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Redacted detail.",
          title: "Message",
        },
        retryable: {
          const: false,
          default: false,
          description: "Retry policy.",
          title: "Retryable",
          type: "boolean",
        },
      },
      required: ["code"],
      title: "InternalError",
      type: "object",
    },
    InterviewPhase: {
      description:
        "Which lifecycle phase of a teaching session a step belongs to. ``setup`` (e.g. a\nlogin/navigation sequence) runs first and ``teardown`` runs last; ``main`` is the repeatable\ntask in between. At finalize the taught steps are kept in one composed SOP, tagged with their\nphase and ordered setup → main → teardown (the composition UI groups them into zones and replay\nruns the setup steps first). A session that never switches phase produces a single all-``main``\nSOP (backward compatible). Mirrors ``InterviewPhaseSchema`` in ``@erp/rpc``.",
      enum: ["setup", "main", "teardown"],
      title: "InterviewPhase",
      type: "string",
    },
    InvalidConfigError: {
      additionalProperties: false,
      description: "The request is invalid and must change before retrying.",
      properties: {
        code: {
          const: "invalid_config",
          description: "Error discriminator.",
          title: "Code",
          type: "string",
        },
        message: {
          anyOf: [
            {
              maxLength: 300,
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Redacted detail.",
          title: "Message",
        },
        retryable: {
          const: false,
          default: false,
          description: "Retry policy.",
          title: "Retryable",
          type: "boolean",
        },
      },
      required: ["code"],
      title: "InvalidConfigError",
      type: "object",
    },
    JevModelConfig: {
      additionalProperties: false,
      description:
        "The experimental Jev Driver: it reads the page as numbered elements and asks Jev to\nchoose an operation and an element each step. Not a computer-use model, so it takes no\ngeneration limits. Offered to `operator.browser_use` only.",
      properties: {
        modelId: {
          const: "jev",
          description: "The Jev model served by the LiteLLM proxy.",
          title: "Modelid",
          type: "string",
        },
        provider: {
          const: "jev",
          default: "jev",
          description:
            "Provider discriminator selecting the experimental Jev Driver.",
          title: "Provider",
          type: "string",
        },
        slowPageWaitSeconds: {
          default: 3,
          description:
            "How long a page that is still loading may take to show new controls before the Jev Driver accepts that it is blocked.",
          maximum: 30,
          minimum: 0,
          title: "Slowpagewaitseconds",
          type: "number",
        },
      },
      required: ["modelId"],
      title: "JevModelConfig",
      type: "object",
    },
    JsonObject: {
      additionalProperties: {
        $ref: "#/$defs/JsonValue",
      },
      description:
        "String-keyed JSON object whose values are recursively valid JSON.",
      type: "object",
    },
    JsonScalar: {
      anyOf: [
        {
          type: "string",
        },
        {
          type: "integer",
        },
        {
          type: "number",
        },
        {
          type: "boolean",
        },
        {
          type: "null",
        },
      ],
      description: "JSON primitive value: string, number, boolean, or null.",
    },
    JsonValue: {
      anyOf: [
        {
          $ref: "#/$defs/JsonScalar",
        },
        {
          items: {
            $ref: "#/$defs/JsonValue",
          },
          type: "array",
        },
        {
          additionalProperties: {
            $ref: "#/$defs/JsonValue",
          },
          type: "object",
        },
      ],
      description:
        "Recursively valid JSON value composed only of primitives, arrays, and string-keyed objects.",
    },
    KnownValue: {
      additionalProperties: false,
      description:
        "A value the step hands the Operator before it starts: one of its connected data inputs.\n\nThe Jev Driver types only Known Values (``TYPE_VALUE``), chosen by key; it never composes\ntext. Credentials are never Known Values; they reach a page only through Credential Fill.",
      properties: {
        key: {
          description:
            "The data input's key, as referenced by {{key}} in the Instructions.",
          minLength: 1,
          pattern:
            "[^\\u0009-\\u000d\\u001c-\\u0020\\u0085\\u00a0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000]",
          title: "Key",
          type: "string",
        },
        label: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "The data input's human label; null shows the key alone.",
          title: "Label",
        },
        value: {
          description: "The exact text typed when this Known Value is chosen.",
          title: "Value",
          type: "string",
        },
      },
      required: ["key", "value"],
      title: "KnownValue",
      type: "object",
    },
    MockUiSessionInput: {
      additionalProperties: false,
      description:
        "Closed environment and seed configuration accepted by the mock-studio skill.",
      properties: {
        confirmBrowser: {
          anyOf: [
            {
              $ref: "#/$defs/ConfirmBrowserConfig",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Optional interactive Kernel browser used for human action-confirmation pauses; null degrades them to passive text review.",
        },
        credentials: {
          $ref: "#/$defs/CredentialsConfig",
          description:
            "By-reference credentials available to the studio without exposing secret values in its A2A payload.",
        },
        gitRef: {
          default: "main",
          description:
            "Git branch, tag, or commit checked out when seeding from a repository.",
          minLength: 1,
          title: "Gitref",
          type: "string",
        },
        previewPort: {
          default: 8000,
          description:
            "Sandbox TCP port on which the mock's static preview server listens.",
          maximum: 65535,
          minimum: 1,
          title: "Previewport",
          type: "integer",
        },
        procedureParameterValues: {
          additionalProperties: {
            type: "string",
          },
          description:
            "Concrete procedure parameter bindings stamped onto generated evaluation cases.",
          title: "Procedureparametervalues",
          type: "object",
        },
        procedureParameters: {
          description:
            "Authoritative procedure parameter declarations stamped onto generated evaluation cases.",
          items: {
            $ref: "#/$defs/GraphParameter",
          },
          title: "Procedureparameters",
          type: "array",
        },
        repoUrl: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Optional Git repository used to seed the sandbox; null selects the server-configured repository or local upload fallback.",
          title: "Repourl",
        },
        seedSite: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Optional bootstrap site whose files seed a new durable mock; null uses the target site's normal seed resolution.",
          title: "Seedsite",
        },
        site: {
          default: "famous-mock-ui",
          description:
            "Stable mock-site slug identifying the durable self-hosted mock to edit.",
          minLength: 1,
          title: "Site",
          type: "string",
        },
        storyboard: {
          description:
            "Ordered screen requirements aligned by index with source-screenshot FileParts; empty requests a non-storyboard build.",
          items: {
            type: "string",
          },
          title: "Storyboard",
          type: "array",
        },
        storyboardEdges: {
          description:
            "Explicit transitions between storyboard screens; empty derives the linear chain from storyboard order.",
          items: {
            $ref: "#/$defs/StoryboardEdge",
          },
          title: "Storyboardedges",
          type: "array",
        },
        trajectorySeed: {
          anyOf: [
            {
              $ref: "#/$defs/MockUiTrajectorySeedInput",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Completed SOP-capture trajectory used to derive storyboard screens when explicit storyboard notes are absent.",
        },
        version: {
          anyOf: [
            {
              minimum: 1,
              type: "integer",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Positive catalog version being built and published; null denotes a scratch or uncataloged build.",
          title: "Version",
        },
      },
      title: "MockUiSessionInput",
      type: "object",
    },
    MockUiStudioArtifact: {
      anyOf: [
        {
          $ref: "#/$defs/ResultTextArtifact",
        },
        {
          $ref: "#/$defs/DiffArtifact",
        },
        {
          $ref: "#/$defs/FileArtifact",
        },
        {
          $ref: "#/$defs/MockUiStudioResultArtifact",
        },
        {
          $ref: "#/$defs/StateGraphArtifact",
        },
        {
          $ref: "#/$defs/PathsArtifact",
        },
      ],
      description:
        "Mock-studio summary, source change, edited file, structured result, state graph, or evaluation-path deliverable.",
    },
    MockUiStudioInput: {
      additionalProperties: false,
      description:
        "Initial configuration for the sandbox-backed mock UI studio.",
      properties: {
        agent: {
          $ref: "#/$defs/AgentConfig",
          description:
            "Coding-agent provider, model, and execution limits; defaults are tuned for full-screen mock generation.",
        },
        session: {
          $ref: "#/$defs/MockUiSessionInput",
          description:
            "Mock selection, seed source, preview, and storyboard environment.",
        },
      },
      title: "MockUiStudioInput",
      type: "object",
    },
    MockUiStudioOutput: {
      additionalProperties: false,
      description: "Terminal summary for an approved mock UI build.",
      properties: {
        editedFiles: {
          description: "Mock-relative paths changed by the studio.",
          items: {
            type: "string",
          },
          title: "Editedfiles",
          type: "array",
        },
        hostedUrl: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Durable published preview URL when publication succeeded.",
          title: "Hostedurl",
        },
        site: {
          default: "",
          description: "Stable mock site slug.",
          title: "Site",
          type: "string",
        },
        summary: {
          description: "Human-readable redacted build summary.",
          title: "Summary",
          type: "string",
        },
      },
      required: ["summary"],
      title: "MockUiStudioOutput",
      type: "object",
    },
    MockUiStudioPause: {
      description:
        "Reason mock-studio editing parked for passive review or an interactive human confirmation action.",
      discriminator: {
        mapping: {
          confirm_action: "#/$defs/ConfirmActionPause",
          review: "#/$defs/ReviewPause",
        },
        propertyName: "kind",
      },
      oneOf: [
        {
          $ref: "#/$defs/ReviewPause",
        },
        {
          $ref: "#/$defs/ConfirmActionPause",
        },
      ],
    },
    MockUiStudioResultArtifact: {
      additionalProperties: false,
      description: "Mock-studio structured terminal result artifact.",
      properties: {
        data: {
          $ref: "#/$defs/MockUiStudioOutput",
          description: "Typed mock-studio result.",
        },
        name: {
          const: "result_json",
          description: "Artifact discriminator.",
          title: "Name",
          type: "string",
        },
      },
      required: ["name", "data"],
      title: "MockUiStudioResultArtifact",
      type: "object",
    },
    MockUiStudioResume: {
      description:
        "Human feedback or interactive takeover result that resumes a parked mock-studio editing turn.",
      discriminator: {
        mapping: {
          answer: "#/$defs/TextResume",
          takeover: "#/$defs/TakeoverResume",
        },
        propertyName: "kind",
      },
      oneOf: [
        {
          $ref: "#/$defs/TextResume",
        },
        {
          $ref: "#/$defs/TakeoverResume",
        },
      ],
    },
    MockUiStudioSignal: {
      description: "Mock-studio editing progress or advisory liveness signal.",
      discriminator: {
        mapping: {
          heartbeat: "#/$defs/HeartbeatSignal",
          step: "#/$defs/StepSignal",
        },
        propertyName: "kind",
      },
      oneOf: [
        {
          $ref: "#/$defs/StepSignal",
        },
        {
          $ref: "#/$defs/HeartbeatSignal",
        },
      ],
    },
    MockUiTrajectorySeedInput: {
      additionalProperties: false,
      description:
        "Closed author-SOP handoff accepted by the mock-studio skill.\n\nFrames and branches deliberately reuse the exact DTOs emitted by ``AuthorSopOutput``. The\nhandoff therefore cannot silently discard gesture coordinates or relax path constraints.",
      properties: {
        branches: {
          description:
            "Alternate trajectory paths; empty denotes one linear walkthrough.",
          items: {
            $ref: "#/$defs/AuthoredTrajectoryPath",
          },
          title: "Branches",
          type: "array",
        },
        frames: {
          description:
            "Captured walkthrough frames in screenshot/FilePart order.",
          items: {
            $ref: "#/$defs/TrajectoryFrame",
          },
          title: "Frames",
          type: "array",
        },
        steps: {
          description:
            "Minimal projections of the SOP steps taught by the walkthrough.",
          items: {
            $ref: "#/$defs/MockUiTrajectoryStepInput",
          },
          title: "Steps",
          type: "array",
        },
      },
      title: "MockUiTrajectorySeedInput",
      type: "object",
    },
    MockUiTrajectoryStepInput: {
      additionalProperties: false,
      description:
        "Closed SOP-step projection accepted in a mock trajectory seed.",
      properties: {
        constraints: {
          description: "RFC 2119 constraints captured for the taught SOP step.",
          items: {
            type: "string",
          },
          title: "Constraints",
          type: "array",
        },
        description: {
          default: "",
          description: "Detailed instruction captured for the taught SOP step.",
          title: "Description",
          type: "string",
        },
        name: {
          default: "",
          description: "Human-readable name of the taught SOP step.",
          title: "Name",
          type: "string",
        },
        stepId: {
          default: "",
          description: "Stable id of the taught SOP step.",
          title: "Stepid",
          type: "string",
        },
      },
      title: "MockUiTrajectoryStepInput",
      type: "object",
    },
    ModelUnavailableError: {
      additionalProperties: false,
      description:
        "The model connection kept failing mid-response (a dropped stream, or the provider\nreporting overload inside an open stream) and the service's bounded per-turn retries ran\nout.\n\nRetryable because nothing about the request is wrong: an identical run plausibly succeeds\nonce the link recovers. The run may have acted on the screen before the drop and those\nactions are not rolled back, so a consumer retrying a write-bearing task should check the\ntarget application's state first; ``message`` says what progress the service could vouch\nfor.",
      properties: {
        code: {
          const: "model_unavailable",
          description: "Error discriminator.",
          title: "Code",
          type: "string",
        },
        message: {
          anyOf: [
            {
              maxLength: 300,
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Redacted detail.",
          title: "Message",
        },
        retryable: {
          const: true,
          default: true,
          description: "Retry policy.",
          title: "Retryable",
          type: "boolean",
        },
      },
      required: ["code"],
      title: "ModelUnavailableError",
      type: "object",
    },
    OpenAIModelConfig: {
      additionalProperties: false,
      description: "OpenAI computer-use model selection and generation limits.",
      properties: {
        maxTokens: {
          default: 16384,
          description:
            "Maximum output-token budget for each computer-use model turn.",
          minimum: 1,
          title: "Maxtokens",
          type: "integer",
        },
        modelId: {
          description: "Allow-listed OpenAI computer-use model identifier.",
          enum: [
            "gpt-6.1-sol",
            "gpt-6-astra",
            "gpt-5.6-terra",
            "gpt-5.6-luna",
            "gpt-5.6-sol",
            "gpt-5.5",
            "gpt-5.4",
            "gpt-5.4-mini",
          ],
          title: "Modelid",
          type: "string",
        },
        provider: {
          const: "openai",
          default: "openai",
          description: "Provider discriminator selecting the OpenAI driver.",
          title: "Provider",
          type: "string",
        },
        thinkingBudget: {
          default: 1024,
          description:
            "Requested extended-thinking token budget; currently consumed by Anthropic and ignored by providers without an equivalent control.",
          minimum: 0,
          title: "Thinkingbudget",
          type: "integer",
        },
      },
      required: ["modelId"],
      title: "OpenAIModelConfig",
      type: "object",
    },
    OperatorAgentConfig: {
      additionalProperties: false,
      description:
        "`AgentConfig` for `operator.browser_use`: its model may also select the Jev Driver.",
      properties: {
        execution: {
          $ref: "#/$defs/ExecutionConfig",
          description:
            "Loop iteration and wall-clock safety bounds for each turn.",
        },
        model: {
          description:
            "Provider-discriminated computer-use model and generation limits, or the experimental Jev Driver.",
          discriminator: {
            mapping: {
              anthropic: "#/$defs/AnthropicModelConfig",
              gemini: "#/$defs/GeminiModelConfig",
              jev: "#/$defs/JevModelConfig",
              openai: "#/$defs/OpenAIModelConfig",
            },
            propertyName: "provider",
          },
          oneOf: [
            {
              $ref: "#/$defs/AnthropicModelConfig",
            },
            {
              $ref: "#/$defs/OpenAIModelConfig",
            },
            {
              $ref: "#/$defs/GeminiModelConfig",
            },
            {
              $ref: "#/$defs/JevModelConfig",
            },
          ],
          title: "Model",
        },
        resultSchema: {
          anyOf: [
            {
              additionalProperties: true,
              type: "object",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Optional JSON Schema that the done tool's caller-defined structured result must satisfy; null requests only a free-text summary.",
          title: "Resultschema",
        },
        visualOnly: {
          default: false,
          description:
            "Whether the agent must reason from screenshots alone, without DOM, accessibility-tree, or selector-based perception.",
          title: "Visualonly",
          type: "boolean",
        },
      },
      required: ["model"],
      title: "OperatorAgentConfig",
      type: "object",
    },
    OperatorStep: {
      additionalProperties: false,
      description:
        "Durable, self-contained record of one agent action boundary.",
      properties: {
        actions: {
          description: "Actions selected by the generation.",
          items: {
            $ref: "#/$defs/OperatorStepAction",
          },
          title: "Actions",
          type: "array",
        },
        generation: {
          $ref: "#/$defs/OperatorStepGeneration",
          description: "Generation that selected the actions.",
        },
        index: {
          description: "Monotonic zero-based action index within the A2A task.",
          minimum: 0,
          title: "Index",
          type: "integer",
        },
        isTerminal: {
          default: false,
          description:
            "Whether this action intentionally ended the agent loop.",
          title: "Isterminal",
          type: "boolean",
        },
        messageId: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Reasoning message id associated with this action.",
          title: "Messageid",
        },
        redaction: {
          $ref: "#/$defs/OperatorStepRedaction",
          description: "Redaction evidence for this step.",
        },
        schemaVersion: {
          const: 1,
          default: 1,
          description: "Operator-step sub-schema version.",
          title: "Schemaversion",
          type: "integer",
        },
        status: {
          $ref: "#/$defs/OperatorStepStatus",
          default: "ok",
          description: "Aggregate step outcome.",
        },
      },
      required: ["index", "generation", "actions", "redaction"],
      title: "OperatorStep",
      type: "object",
    },
    OperatorStepAction: {
      additionalProperties: false,
      description:
        "One redacted tool invocation in the canonical operator trace.",
      properties: {
        args: {
          anyOf: [
            {
              $ref: "#/$defs/JsonObject",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Redacted JSON action arguments.",
        },
        result: {
          $ref: "#/$defs/OperatorStepActionResult",
          description: "Observed action outcome.",
        },
        tool: {
          description: "Stable, display-safe tool/action identifier.",
          minLength: 1,
          title: "Tool",
          type: "string",
        },
      },
      required: ["tool", "result"],
      title: "OperatorStepAction",
      type: "object",
    },
    OperatorStepActionResult: {
      additionalProperties: false,
      description: "Observed result of one tool action.",
      properties: {
        screenshotRef: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "URI of the frame after the action; image bytes never ride here.",
          title: "Screenshotref",
        },
        status: {
          $ref: "#/$defs/OperatorStepStatus",
          default: "ok",
          description: "Action outcome.",
        },
      },
      title: "OperatorStepActionResult",
      type: "object",
    },
    OperatorStepGeneration: {
      additionalProperties: false,
      description: "The model generation that selected one or more actions.",
      properties: {
        modelFree: {
          default: false,
          description:
            "True when the producer positively asserts no model produced this step — a deterministic replay step, or the hardcoded AVD login sequence. This is what disambiguates a null 'modelId': true means the absence is intentional and the step must stay unattributed, false means the producer may simply predate the field. A consumer must not substitute a requested or configured model when this is true.",
          title: "Modelfree",
          type: "boolean",
        },
        modelId: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Concrete provider model that produced this generation, e.g. 'claude-sonnet-5-5'. Null means EITHER no model produced the step (see 'modelFree') OR the producer predates this field — a consumer must never read its absence as the run's configured model.",
          title: "Modelid",
        },
        reasoning: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Redacted reasoning text when the provider exposes it.",
          title: "Reasoning",
        },
        thinking: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Redacted model thinking (extended thinking) that preceded this action, when the provider exposes it.",
          title: "Thinking",
        },
        tokens: {
          anyOf: [
            {
              $ref: "#/$defs/OperatorStepTokens",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Generation token usage when available.",
        },
      },
      title: "OperatorStepGeneration",
      type: "object",
    },
    OperatorStepRedaction: {
      additionalProperties: false,
      description:
        "Evidence that secret scrubbing ran before the step crossed the wire.",
      properties: {
        applied: {
          description: "Whether redaction was applied before emission.",
          title: "Applied",
          type: "boolean",
        },
        fields: {
          anyOf: [
            {
              items: {
                type: "string",
              },
              type: "array",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Optional paths whose values were redacted.",
          title: "Fields",
        },
      },
      required: ["applied"],
      title: "OperatorStepRedaction",
      type: "object",
    },
    OperatorStepStatus: {
      description: "Aggregate outcome of a canonical operator action boundary.",
      enum: ["ok", "error", "terminal"],
      title: "OperatorStepStatus",
      type: "string",
    },
    OperatorStepTokens: {
      additionalProperties: false,
      description: "Token accounting attached to one model generation.",
      properties: {
        cacheRead: {
          anyOf: [
            {
              minimum: 0,
              type: "integer",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Input tokens served from provider cache.",
          title: "Cacheread",
        },
        cacheWrite: {
          anyOf: [
            {
              minimum: 0,
              type: "integer",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Input tokens written to provider cache.",
          title: "Cachewrite",
        },
        input: {
          anyOf: [
            {
              minimum: 0,
              type: "integer",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Input tokens billed.",
          title: "Input",
        },
        output: {
          anyOf: [
            {
              minimum: 0,
              type: "integer",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Output tokens billed.",
          title: "Output",
        },
      },
      title: "OperatorStepTokens",
      type: "object",
    },
    OperatorTakeoverPause: {
      additionalProperties: false,
      description: "The human requested temporary control of the live browser.",
      properties: {
        kind: {
          const: "operator_takeover",
          description: "Pause discriminator.",
          title: "Kind",
          type: "string",
        },
      },
      required: ["kind"],
      title: "OperatorTakeoverPause",
      type: "object",
    },
    PathsArtifact: {
      additionalProperties: false,
      description: "Mock studio evaluation-case forest.",
      properties: {
        data: {
          $ref: "#/$defs/EvalPaths",
          description: "Evaluation cases plus optional hosted URL.",
        },
        name: {
          const: "paths",
          description: "Artifact discriminator.",
          title: "Name",
          type: "string",
        },
      },
      required: ["name", "data"],
      title: "PathsArtifact",
      type: "object",
    },
    PausePolicy: {
      description:
        "Autonomy policy controlling when SOP authoring pauses for human confirmation.",
      enum: ["every_step", "high_risk_only", "autonomous"],
      title: "PausePolicy",
      type: "string",
    },
    ProcedureInput: {
      additionalProperties: false,
      description:
        "One procedure the operator may use: a ``name`` the model calls via ``run_procedure``\n(replay) or an ``id`` it fetches via ``get_procedure`` (read + follow), plus its flattened,\nparameter-resolved plan. Node resolves composition + params at message-build time via\n``resolveSopPlan`` (same as a standalone replay); steps reuse ``RunSopStepInput`` so the\noperator's inline replay matches ``operator.run_sop``. ``id``/``title``/``body`` default empty\nfor back-compat with older Node payloads that sent only ``name`` + ``steps``.",
      properties: {
        body: {
          default: "",
          description:
            "Authored procedure markdown available when the model reads and follows the SOP instead of replaying captured steps.",
          title: "Body",
          type: "string",
        },
        guide: {
          anyOf: [
            {
              $ref: "#/$defs/GuideInput",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Reference screenshots for this procedure's inline replay; their images ride the browser-use message as named FileParts.",
        },
        id: {
          default: "",
          description:
            "Stable persisted SOP id used by procedure lookup; empty falls back to the procedure name for legacy requests.",
          title: "Id",
          type: "string",
        },
        maxIterations: {
          anyOf: [
            {
              maximum: 200,
              minimum: 1,
              type: "integer",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Maximum agent actions one agentic step of this procedure may take before the step is cancelled. Null falls back to the running agent's own iteration cap.",
          title: "Maxiterations",
        },
        mode: {
          default: "library",
          description:
            "Task-playbook shelf: required (prescribed for the task), suggested (offered as a hint, read on demand), or library (rest of the allowlist).",
          enum: ["required", "suggested", "library"],
          title: "Mode",
          type: "string",
        },
        name: {
          description:
            "Stable model-facing procedure name used by list, get, and run procedure tools.",
          title: "Name",
          type: "string",
        },
        ordinal: {
          anyOf: [
            {
              type: "integer",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Position within its shelf, preserving the task owner's ordering.",
          title: "Ordinal",
        },
        outputs: {
          default: [],
          description:
            "Declared values an agentic step of this procedure may read off the screen and report; reported values are appended to the run_procedure tool result.",
          items: {
            $ref: "#/$defs/SopOutput",
          },
          title: "Outputs",
          type: "array",
        },
        phase: {
          default: "main",
          description:
            "Task-loop ownership: setup/teardown are loop-owned; main is agent-owned.",
          enum: ["setup", "main", "teardown"],
          title: "Phase",
          type: "string",
        },
        readableInstructions: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Instructions rendered from this resolved version's canonical steps and parameters. When supplied, procedure reads use this instead of the legacy authored body.",
          title: "Readableinstructions",
        },
        sessionRules: {
          description:
            "Versioned SOP rules that remain active throughout the task.",
          items: {
            maxLength: 4000,
            minLength: 1,
            pattern:
              "^[^\\u0000-\\u001f\\u007f-\\u009f\\u2028\\u2029]*[^\\u0000-\\u001f\\u007f-\\u009f\\u2028\\u2029\\u0009-\\u000d\\u001c-\\u0020\\u0085\\u00a0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000][^\\u0000-\\u001f\\u007f-\\u009f\\u2028\\u2029]*$",
            type: "string",
          },
          maxItems: 50,
          title: "Sessionrules",
          type: "array",
        },
        steps: {
          description:
            "Flattened, parameter-resolved replay plan for this procedure.",
          items: {
            $ref: "#/$defs/RunSopStepInput",
          },
          title: "Steps",
          type: "array",
        },
        timeoutSeconds: {
          default: 1200,
          description:
            "Wall-clock budget for the whole procedure replay, in seconds.",
          maximum: 7200,
          minimum: 10,
          title: "Timeoutseconds",
          type: "integer",
        },
        title: {
          default: "",
          description:
            "Human-readable authored procedure title exposed to the model.",
          title: "Title",
          type: "string",
        },
        version: {
          anyOf: [
            {
              minimum: 1,
              type: "integer",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Resolved SOP version being replayed.",
          title: "Version",
        },
        visualOnly: {
          default: false,
          description:
            "Whether this procedure must be replayed from the screen rather than the DOM.",
          title: "Visualonly",
          type: "boolean",
        },
        whenToUse: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "One-line disclosure hint for a suggested procedure — the condition under which the model should read it.",
          title: "Whentouse",
        },
      },
      required: ["name", "steps"],
      title: "ProcedureInput",
      type: "object",
    },
    ProcedureRun: {
      additionalProperties: false,
      description:
        "One inline ``run_procedure`` replay the operator performed during its turn (task\nplaybooks): which procedure, whether it finished, and how much it drifted. Node records each\nagainst the core task the turn worked for, so a required procedure replayed mid-turn\nsatisfies the task's completion gate like a standalone replay would.",
      properties: {
        agentic: {
          description: "Steps intentionally executed agentically.",
          minimum: 0,
          title: "Agentic",
          type: "integer",
        },
        healed: {
          description: "Drifted steps recovered and patched.",
          minimum: 0,
          title: "Healed",
          type: "integer",
        },
        id: {
          description: "Stable persisted SOP id of the replayed procedure.",
          title: "Id",
          type: "string",
        },
        name: {
          description: "Model-facing procedure name that was replayed.",
          title: "Name",
          type: "string",
        },
        status: {
          description: "Replay outcome.",
          enum: ["succeeded", "failed"],
          title: "Status",
          type: "string",
        },
        version: {
          anyOf: [
            {
              minimum: 1,
              type: "integer",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Actual resolved SOP version replayed.",
          title: "Version",
        },
      },
      required: ["id", "name", "status", "healed", "agentic"],
      title: "ProcedureRun",
      type: "object",
    },
    QuestionPause: {
      additionalProperties: false,
      description: "The skill needs a plain human answer.",
      properties: {
        kind: {
          const: "question",
          description: "Pause discriminator.",
          title: "Kind",
          type: "string",
        },
      },
      required: ["kind"],
      title: "QuestionPause",
      type: "object",
    },
    RemoteDesktopLoginConfig: {
      additionalProperties: false,
      description:
        "Drives a hardcoded AVD / Windows-365 login before the agent loop.\n\nThe portal login (Microsoft email/password) uses the session's primary\n``credentials.credential_set_id``; this block adds the desktop tile to open,\nthe in-session VM password (a SEPARATE by-reference credential), and an\noptional MFA/OTP target the operator resolves live via Node's\n``credentialsInternal.resolveOtp``. Credential VALUES never cross the wire\n(spec §13) — only references. When set, the operator logs into the remote\ndesktop itself, then the agent loop drives the resulting session.",
      properties: {
        authStateName: {
          anyOf: [
            {
              minLength: 1,
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Name the reusable Azure authentication state is saved under; null falls back to avd-<desktop_name>.",
          title: "Authstatename",
        },
        authStateTargetId: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Agent-definition target under which reusable Azure authentication state is loaded and saved.",
          title: "Authstatetargetid",
        },
        desktopLoadWaitSeconds: {
          default: 10,
          description:
            "Seconds to wait for the remote desktop canvas to settle after it first becomes visible.",
          maximum: 120,
          minimum: 0,
          title: "Desktoploadwaitseconds",
          type: "number",
        },
        desktopName: {
          description:
            "Case-insensitive name fragment used to select the remote desktop tile in the portal.",
          title: "Desktopname",
          type: "string",
        },
        otpTargetId: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Opaque OTP target reference used to resolve an Azure MFA challenge; null supports only sessions that do not prompt for MFA.",
          title: "Otptargetid",
        },
        portalUrl: {
          description:
            "Microsoft AVD or Windows 365 portal URL opened by the pre-run login flow.",
          title: "Portalurl",
          type: "string",
        },
        vmCredentialSetId: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Opaque reference to the separate VM or RDP password credential; null means no in-session password can be supplied.",
          title: "Vmcredentialsetid",
        },
      },
      required: ["portalUrl", "desktopName"],
      title: "RemoteDesktopLoginConfig",
      type: "object",
    },
    Repair: {
      additionalProperties: false,
      description:
        "Fresh durable code captured for a persisted step that drifted.",
      properties: {
        code: {
          description: "Redacted replacement step code.",
          title: "Code",
          type: "string",
        },
        language: {
          $ref: "#/$defs/ScriptLanguage",
          default: "python",
          description: "Replacement code language.",
        },
        mode: {
          default: "self_heal",
          description: "Recovery mechanism that produced the repair.",
          enum: ["self_heal", "agentic_repair"],
          title: "Mode",
          type: "string",
        },
        ownerVersionId: {
          description: "Owning SOP version id.",
          minLength: 1,
          title: "Ownerversionid",
          type: "string",
        },
        stepId: {
          description: "Persisted step id to patch.",
          minLength: 1,
          title: "Stepid",
          type: "string",
        },
        target: {
          anyOf: [
            {
              $ref: "#/$defs/StepTarget",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Durable locator hint captured by the heal — a visual pixel point for a coordinate repair; null for semantic repairs.",
        },
        verified: {
          default: false,
          description:
            "Whether the step's Verify post-condition ran and held after the heal; false when the repair rests on the healer's own success claim.",
          title: "Verified",
          type: "boolean",
        },
      },
      required: ["ownerVersionId", "stepId", "code"],
      title: "Repair",
      type: "object",
    },
    ReplayBranchData: {
      additionalProperties: false,
      description: "Complete path decision for one branch node.",
      properties: {
        index: {
          description: "One-based branch position in the run plan.",
          minimum: 1,
          title: "Index",
          type: "integer",
        },
        label: {
          description: "Human-readable condition selecting the path.",
          title: "Label",
          type: "string",
        },
        mode: {
          description: "Decision mechanism.",
          enum: ["deterministic", "agentic"],
          title: "Mode",
          type: "string",
        },
        name: {
          description: "Human-readable branch step name.",
          title: "Name",
          type: "string",
        },
        pathId: {
          description: "Chosen durable path id.",
          minLength: 1,
          title: "Pathid",
          type: "string",
        },
        procedure: {
          anyOf: [
            {
              $ref: "#/$defs/ReplayProcedureRef",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Owning procedure when the producer replays several per task.",
        },
        stepId: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Persisted branch step id.",
          title: "Stepid",
        },
      },
      required: ["index", "name", "pathId", "label", "mode"],
      title: "ReplayBranchData",
      type: "object",
    },
    ReplayBranchDecision: {
      additionalProperties: false,
      description: "Live announcement of one branch-path decision.",
      properties: {
        kind: {
          const: "sop_replay_branch",
          description: "Signal discriminator.",
          title: "Kind",
          type: "string",
        },
        step: {
          $ref: "#/$defs/ReplayBranchData",
          description: "Chosen path and the branch step that selected it.",
        },
      },
      required: ["kind", "step"],
      title: "ReplayBranchDecision",
      type: "object",
    },
    ReplayProcedureRef: {
      additionalProperties: false,
      description:
        "Which procedure a replay boundary belongs to.\n\nEmitted by ``operator.browser_use``, whose ``run_procedure`` tool can replay more than one\nprocedure in a single task. Step indices restart at 1 per procedure, so ``index`` alone cannot\nseparate two procedures' steps, and ``ordinal`` keeps two runs of the SAME procedure distinct.\nAbsent on ``operator.run_sop``, where the whole task is one procedure and the consumer already\nholds its title.",
      properties: {
        name: {
          description: "Human-readable procedure name.",
          title: "Name",
          type: "string",
        },
        ordinal: {
          default: 1,
          description:
            "One-based position of this procedure among those replayed in the task.",
          minimum: 1,
          title: "Ordinal",
          type: "integer",
        },
      },
      required: ["name"],
      title: "ReplayProcedureRef",
      type: "object",
    },
    ReplayStepCompleted: {
      additionalProperties: false,
      description: "Durable boundary for one completed SOP replay step.",
      properties: {
        kind: {
          const: "sop_replay_step",
          description: "Signal discriminator.",
          title: "Kind",
          type: "string",
        },
        step: {
          $ref: "#/$defs/ReplayStepCompletedData",
          description: "Complete replay-step outcome.",
        },
      },
      required: ["kind", "step"],
      title: "ReplayStepCompleted",
      type: "object",
    },
    ReplayStepCompletedData: {
      additionalProperties: false,
      description: "Complete durable outcome of one replayed SOP step.",
      properties: {
        codeKind: {
          anyOf: [
            {
              $ref: "#/$defs/CodeKind",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Kind of durable code attempted, or null for an agentic step.",
        },
        error: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Redacted failure reason when mode is failed.",
          title: "Error",
        },
        index: {
          description: "One-based position in the flattened run plan.",
          minimum: 1,
          title: "Index",
          type: "integer",
        },
        mode: {
          $ref: "#/$defs/StepMode",
          description: "Execution or recovery path that produced the outcome.",
        },
        name: {
          description: "Human-readable SOP step name.",
          title: "Name",
          type: "string",
        },
        noop: {
          default: false,
          description:
            "Whether an optional absent-target step succeeded without acting.",
          title: "Noop",
          type: "boolean",
        },
        procedure: {
          anyOf: [
            {
              $ref: "#/$defs/ReplayProcedureRef",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Owning procedure when the producer replays several per task.",
        },
        stepId: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Persisted SOP step id; null on the markdown-only compatibility path.",
          title: "Stepid",
        },
        verify: {
          anyOf: [
            {
              $ref: "#/$defs/ReplayStepVerify",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Post-condition grade when the producer supplied one.",
        },
      },
      required: ["index", "name", "mode"],
      title: "ReplayStepCompletedData",
      type: "object",
    },
    ReplayStepStarted: {
      additionalProperties: false,
      description: "State-only announcement that an SOP step began replaying.",
      properties: {
        kind: {
          const: "sop_replay_step_started",
          description: "Signal discriminator.",
          title: "Kind",
          type: "string",
        },
        step: {
          $ref: "#/$defs/ReplayStepStartedData",
          description: "Running-step projection; superseded by completion.",
        },
      },
      required: ["kind", "step"],
      title: "ReplayStepStarted",
      type: "object",
    },
    ReplayStepStartedData: {
      additionalProperties: false,
      description: "Identity of an SOP step that has begun replaying.",
      properties: {
        description: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Authored instruction for this step, when the plan carries one.",
          title: "Description",
        },
        index: {
          description: "One-based position in the flattened run plan.",
          minimum: 1,
          title: "Index",
          type: "integer",
        },
        name: {
          description: "Human-readable SOP step name.",
          title: "Name",
          type: "string",
        },
        procedure: {
          anyOf: [
            {
              $ref: "#/$defs/ReplayProcedureRef",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Owning procedure when the producer replays several per task.",
        },
        stepId: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Persisted SOP step id; null on the markdown-only compatibility path.",
          title: "Stepid",
        },
      },
      required: ["index", "name"],
      title: "ReplayStepStartedData",
      type: "object",
    },
    ReplayStepVerify: {
      additionalProperties: false,
      description:
        "Machine-verifiable post-condition grade for one replayed SOP step.",
      properties: {
        kind: {
          anyOf: [
            {
              $ref: "#/$defs/VerifyKind",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Post-condition kind, or null when the step declared none.",
        },
        passed: {
          description: "Whether the step satisfied its success oracle.",
          title: "Passed",
          type: "boolean",
        },
        ran: {
          description: "Whether the post-condition was executed.",
          title: "Ran",
          type: "boolean",
        },
      },
      required: ["ran", "passed"],
      title: "ReplayStepVerify",
      type: "object",
    },
    ResultTextArtifact: {
      additionalProperties: false,
      description: "The human-readable terminal result artifact.",
      properties: {
        name: {
          const: "result",
          description: "Artifact discriminator.",
          title: "Name",
          type: "string",
        },
        text: {
          description: "Artifact text.",
          title: "Text",
          type: "string",
        },
      },
      required: ["name", "text"],
      title: "ResultTextArtifact",
      type: "object",
    },
    ReviewPause: {
      additionalProperties: false,
      description: "The mock studio is awaiting feedback on a passive preview.",
      properties: {
        kind: {
          const: "review",
          description: "Pause discriminator.",
          title: "Kind",
          type: "string",
        },
        liveViewUrl: {
          description: "Preview URL to show the reviewer.",
          minLength: 1,
          title: "Liveviewurl",
          type: "string",
        },
      },
      required: ["kind", "liveViewUrl"],
      title: "ReviewPause",
      type: "object",
    },
    RiskLevel: {
      description:
        "Effective confirmation risk assigned to a captured action group.",
      enum: ["low", "high"],
      title: "RiskLevel",
      type: "string",
    },
    RunError: {
      description:
        "Structured terminal failure with a code-specific, fail-closed retry policy.",
      discriminator: {
        mapping: {
          credential_rejected: "#/$defs/CredentialRejectedError",
          execution_failed: "#/$defs/ExecutionFailedError",
          internal: "#/$defs/InternalError",
          invalid_config: "#/$defs/InvalidConfigError",
          model_unavailable: "#/$defs/ModelUnavailableError",
          step_failed: "#/$defs/StepFailedError",
          timeout: "#/$defs/RunTimeoutError",
        },
        propertyName: "code",
      },
      oneOf: [
        {
          $ref: "#/$defs/InvalidConfigError",
        },
        {
          $ref: "#/$defs/RunTimeoutError",
        },
        {
          $ref: "#/$defs/ExecutionFailedError",
        },
        {
          $ref: "#/$defs/StepFailedError",
        },
        {
          $ref: "#/$defs/InternalError",
        },
        {
          $ref: "#/$defs/CredentialRejectedError",
        },
        {
          $ref: "#/$defs/ModelUnavailableError",
        },
      ],
    },
    RunSopArtifact: {
      description:
        "Human-readable or structured SOP-replay outcome deliverable.",
      discriminator: {
        mapping: {
          result: "#/$defs/ResultTextArtifact",
          result_json: "#/$defs/RunSopResultArtifact",
        },
        propertyName: "name",
      },
      oneOf: [
        {
          $ref: "#/$defs/ResultTextArtifact",
        },
        {
          $ref: "#/$defs/RunSopResultArtifact",
        },
      ],
    },
    RunSopInput: {
      additionalProperties: false,
      description:
        "Initial configuration for deterministic, self-healing SOP replay.",
      properties: {
        agent: {
          $ref: "#/$defs/AgentConfig",
          description:
            "Model selection and safety bounds used for branch choices and self-healing.",
        },
        codeRuntime: {
          $ref: "#/$defs/CodeRuntimeKind",
          default: "daytona",
          description:
            "Execution isolation for captured step code: Daytona sandbox by default, or explicitly trusted in-process execution.",
        },
        guide: {
          anyOf: [
            {
              $ref: "#/$defs/GuideInput",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Reference screenshots bound to steps, shown to the healer/verifier as in-context exemplars; null when no plan version contributes enabled frames.",
        },
        healMode: {
          $ref: "#/$defs/HealMode",
          default: "semantic",
          description:
            "Recovery policy for drifted steps; automatic mode may additionally attempt one bounded computer-use heal for failed coordinate actions.",
        },
        outputs: {
          default: [],
          description:
            "Declared values an agentic replay step may read off the screen and report; reported values are returned on the run result's outputs.",
          items: {
            $ref: "#/$defs/SopOutput",
          },
          title: "Outputs",
          type: "array",
        },
        session: {
          $ref: "#/$defs/SessionConfig",
          description:
            "Caller-owned browser, credentials, and input environment used by the replay.",
        },
        sessionRules: {
          description:
            "Versioned SOP rules that remain active throughout the task.",
          items: {
            maxLength: 4000,
            minLength: 1,
            pattern:
              "^[^\\u0000-\\u001f\\u007f-\\u009f\\u2028\\u2029]*[^\\u0000-\\u001f\\u007f-\\u009f\\u2028\\u2029\\u0009-\\u000d\\u001c-\\u0020\\u0085\\u00a0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000][^\\u0000-\\u001f\\u007f-\\u009f\\u2028\\u2029]*$",
            type: "string",
          },
          title: "Sessionrules",
          type: "array",
        },
        sopMarkdown: {
          default: "",
          description:
            "Legacy SOP markdown parsed only when no structured steps are supplied.",
          title: "Sopmarkdown",
          type: "string",
        },
        steps: {
          anyOf: [
            {
              items: {
                $ref: "#/$defs/RunSopStepInput",
              },
              type: "array",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Canonical flattened, parameter-resolved replay plan; null enables the legacy markdown fallback.",
          title: "Steps",
        },
      },
      required: ["session", "agent"],
      title: "RunSopInput",
      type: "object",
    },
    RunSopOutput: {
      additionalProperties: false,
      description:
        "Complete replay tallies, repairs, decisions, and optional eval state.",
      properties: {
        agentic: {
          description: "Steps intentionally executed agentically.",
          minimum: 0,
          title: "Agentic",
          type: "integer",
        },
        decisions: {
          description: "Branch decisions in execution order.",
          items: {
            $ref: "#/$defs/BranchDecision",
          },
          title: "Decisions",
          type: "array",
        },
        deterministic: {
          description: "Steps completed from captured code.",
          minimum: 0,
          title: "Deterministic",
          type: "integer",
        },
        evalState: {
          anyOf: [
            {
              $ref: "#/$defs/JsonObject",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Captured mock evaluation state when available.",
        },
        failedStep: {
          anyOf: [
            {
              minimum: 1,
              type: "integer",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "One-based failed step, or null on success.",
          title: "Failedstep",
        },
        healed: {
          description: "Drifted steps recovered and patched.",
          minimum: 0,
          title: "Healed",
          type: "integer",
        },
        outputs: {
          additionalProperties: {
            type: "string",
          },
          description:
            "Redacted values agentic steps reported for the procedure's declared outputs, keyed by output name; present on failure too when a value was read before the failing step.",
          title: "Outputs",
          type: "object",
        },
        repairs: {
          description: "Persistable heal repairs.",
          items: {
            $ref: "#/$defs/Repair",
          },
          title: "Repairs",
          type: "array",
        },
        stepsTotal: {
          description: "Reached non-branch steps.",
          minimum: 0,
          title: "Stepstotal",
          type: "integer",
        },
        summary: {
          description: "Human-readable redacted replay summary.",
          title: "Summary",
          type: "string",
        },
      },
      required: ["summary", "stepsTotal", "deterministic", "healed", "agentic"],
      title: "RunSopOutput",
      type: "object",
    },
    RunSopResultArtifact: {
      additionalProperties: false,
      description: "SOP-replay structured result artifact.",
      properties: {
        data: {
          $ref: "#/$defs/RunSopOutput",
          description: "Typed SOP-replay result.",
        },
        name: {
          const: "result_json",
          description: "Artifact discriminator.",
          title: "Name",
          type: "string",
        },
      },
      required: ["name", "data"],
      title: "RunSopResultArtifact",
      type: "object",
    },
    RunSopSignal: {
      description:
        "SOP-replay action, step lifecycle, branch decision, or advisory liveness signal.",
      discriminator: {
        mapping: {
          heartbeat: "#/$defs/HeartbeatSignal",
          sop_replay_branch: "#/$defs/ReplayBranchDecision",
          sop_replay_step: "#/$defs/ReplayStepCompleted",
          sop_replay_step_started: "#/$defs/ReplayStepStarted",
          step: "#/$defs/StepSignal",
        },
        propertyName: "kind",
      },
      oneOf: [
        {
          $ref: "#/$defs/StepSignal",
        },
        {
          $ref: "#/$defs/HeartbeatSignal",
        },
        {
          $ref: "#/$defs/ReplayStepStarted",
        },
        {
          $ref: "#/$defs/ReplayStepCompleted",
        },
        {
          $ref: "#/$defs/ReplayBranchDecision",
        },
      ],
    },
    RunSopStepInput: {
      additionalProperties: false,
      description:
        "One step of the flattened run plan Node sends (AI-246) — composition + parameters already\nresolved. ``owner_version_id`` + ``step_id`` address the persisted row a self-heal repair\nwrites back to; ``params`` is the resolved scope injected as ``params`` for the step's code.",
      properties: {
        action: {
          anyOf: [
            {
              $ref: "#/$defs/StepAction",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Captured executable action; null makes the step agentic unless it is a branch node.",
        },
        bestEffort: {
          default: false,
          description:
            "Whether an absent target is a successful no-op instead of a replay failure.",
          title: "Besteffort",
          type: "boolean",
        },
        branchPathId: {
          default: "",
          description:
            "Branch-path id containing this action step; empty when the action is not a path member.",
          title: "Branchpathid",
          type: "string",
        },
        branchStepId: {
          default: "",
          description:
            "Branch-node step id that owns this action step; empty when the action is not inside a branch.",
          title: "Branchstepid",
          type: "string",
        },
        condition: {
          default: "",
          description:
            "Human-readable decision question carried by a branch node.",
          title: "Condition",
          type: "string",
        },
        constraints: {
          default: [],
          description:
            "Additional behavioral constraints the replay or healer must honor.",
          items: {
            type: "string",
          },
          title: "Constraints",
          type: "array",
        },
        description: {
          default: "",
          description:
            "Detailed instruction or intent for performing the step.",
          title: "Description",
          type: "string",
        },
        kind: {
          $ref: "#/$defs/SopStepKind",
          default: "action",
          description:
            "Step discriminator separating executable actions from branch nodes.",
        },
        name: {
          description: "Human-readable step name shown in replay progress.",
          title: "Name",
          type: "string",
        },
        ownerVersionId: {
          description:
            "Owning SOP version id used to address a durable self-heal repair.",
          title: "Ownerversionid",
          type: "string",
        },
        params: {
          additionalProperties: {
            type: "string",
          },
          default: {},
          description:
            "Fully resolved string parameters injected into this step's captured code and agentic context.",
          title: "Params",
          type: "object",
        },
        paths: {
          default: [],
          description: "Ordered alternatives available from a branch node.",
          items: {
            $ref: "#/$defs/BranchPathInput",
          },
          title: "Paths",
          type: "array",
        },
        sensitive: {
          default: false,
          description:
            "Whether the step may expose a secret, suppressing its screenshot from the emitted replay trace.",
          title: "Sensitive",
          type: "boolean",
        },
        stepId: {
          description:
            "Persisted SOP step id used for progress and repair patch-back.",
          title: "Stepid",
          type: "string",
        },
        target: {
          anyOf: [
            {
              $ref: "#/$defs/StepTarget",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Durable locator hint captured by a prior run; a visual pixel point aims self-heal.",
        },
        verify: {
          anyOf: [
            {
              $ref: "#/$defs/StepVerify",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Optional post-condition used to grade replay success.",
        },
      },
      required: ["stepId", "ownerVersionId", "name"],
      title: "RunSopStepInput",
      type: "object",
    },
    RunTimeoutError: {
      additionalProperties: false,
      description: "The run exceeded its configured time budget.",
      properties: {
        code: {
          const: "timeout",
          description: "Error discriminator.",
          title: "Code",
          type: "string",
        },
        message: {
          anyOf: [
            {
              maxLength: 300,
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Redacted detail.",
          title: "Message",
        },
        retryable: {
          const: true,
          default: true,
          description: "Retry policy.",
          title: "Retryable",
          type: "boolean",
        },
      },
      required: ["code"],
      title: "RunTimeoutError",
      type: "object",
    },
    ScriptLanguage: {
      description:
        "Programming language accepted for captured deterministic browser code.",
      enum: ["python", "js"],
      title: "ScriptLanguage",
      type: "string",
    },
    SelectorHealth: {
      description:
        "Assessment of whether a captured selector is durable or likely to drift.",
      enum: ["durable", "fragile"],
      title: "SelectorHealth",
      type: "string",
    },
    SessionConfig: {
      additionalProperties: false,
      description: "The environment Node provides.",
      properties: {
        browser: {
          $ref: "#/$defs/BrowserConfig",
          description:
            "Caller-owned browser connection and viewport configuration.",
        },
        credentials: {
          $ref: "#/$defs/CredentialsConfig",
          description:
            "By-reference credentials the operator may resolve without exposing secret values on the wire.",
        },
        input: {
          $ref: "#/$defs/InputConfig",
          description:
            "Keyboard and pointer pacing for the display-control layer.",
        },
        remoteDesktopLogin: {
          anyOf: [
            {
              $ref: "#/$defs/RemoteDesktopLoginConfig",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Optional pre-agent Microsoft remote-desktop login workflow; null starts the agent directly in the supplied browser.",
        },
      },
      required: ["browser"],
      title: "SessionConfig",
      type: "object",
    },
    SetPhaseResume: {
      additionalProperties: false,
      description:
        "Change which lifecycle phase subsequent SOP captures belong to.",
      properties: {
        kind: {
          const: "set_phase",
          description: "Resume-directive discriminator.",
          title: "Kind",
          type: "string",
        },
        phase: {
          description: "Lifecycle phase assigned to later captured steps.",
          enum: ["setup", "main", "teardown"],
          title: "Phase",
          type: "string",
        },
      },
      required: ["kind", "phase"],
      title: "SetPhaseResume",
      type: "object",
    },
    SkillId: {
      description: "Stable routing identifiers placed in A2A message metadata.",
      enum: [
        "operator.browser_use",
        "operator.author_sop",
        "operator.run_sop",
        "mock_ui.studio",
      ],
      title: "SkillId",
      type: "string",
    },
    SopBranch: {
      additionalProperties: false,
      description:
        "A branch step's payload: the plain-English ``condition`` plus ≥2 labelled paths. Path order\nis meaningful — replay evaluates checks in order and a check-less default path wins last.",
      properties: {
        condition: {
          description:
            "Non-blank, single-line decision question that distinguishes the paths and contains no reserved markdown separators.",
          minLength: 1,
          pattern:
            "^(?![\\u0009-\\u000d\\u001c-\\u0020\\u0085\\u00a0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000])(?![\\s\\S]*[\\u0009-\\u000d\\u001c-\\u0020\\u0085\\u00a0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000]$)(?![\\s\\S]*[\\r\\n\\u0085\\u2028\\u2029])(?![\\s\\S]*→)(?![\\s\\S]* · )[\\s\\S]+$",
          title: "Condition",
          type: "string",
        },
        paths: {
          description:
            "At least two ordered branch alternatives with unique path ids and labels; checked paths are evaluated in order and a default path is evaluated last.",
          items: {
            $ref: "#/$defs/SopBranchPath",
          },
          minItems: 2,
          title: "Paths",
          type: "array",
          "x-uniqueProperties": ["pathId", "label"],
        },
      },
      required: ["condition", "paths"],
      title: "SopBranch",
      type: "object",
    },
    SopBranchPath: {
      additionalProperties: false,
      description:
        "One labelled alternative of a branch step. ``step_ids`` bind the ordinary flat action steps\nthat belong to this path (the branch step only *binds* them — members stay normal ``### N.``\nsteps). ``path_id`` is durable addressing metadata and never renders into the markdown; the\nhuman-readable ``label`` is what renders (and what the agentic chooser matches).",
      properties: {
        check: {
          anyOf: [
            {
              $ref: "#/$defs/ai_service__a2a__author_sop__draft__models__BranchCheck",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Optional deterministic predicate selecting this path; null denotes a default or agentically selected path.",
        },
        label: {
          description:
            "Non-blank, single-line condition or outcome label; unique within its parent branch and free of reserved markdown separators.",
          minLength: 1,
          pattern:
            "^(?![\\u0009-\\u000d\\u001c-\\u0020\\u0085\\u00a0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000])(?![\\s\\S]*[\\u0009-\\u000d\\u001c-\\u0020\\u0085\\u00a0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000]$)(?![\\s\\S]*[\\r\\n\\u0085\\u2028\\u2029])(?![\\s\\S]*→)(?![\\s\\S]* · )[\\s\\S]+$",
          title: "Label",
          type: "string",
        },
        pathId: {
          description:
            "Durable identifier of this alternate path; unique within its parent branch.",
          minLength: 1,
          title: "Pathid",
          type: "string",
        },
        stepIds: {
          default: [],
          description:
            "Stable ids of ordinary SOP steps belonging to this path.",
          items: {
            type: "string",
          },
          title: "Stepids",
          type: "array",
        },
      },
      required: ["pathId", "label"],
      title: "SopBranchPath",
      type: "object",
    },
    SopOutput: {
      additionalProperties: false,
      description:
        "One declared procedure OUTPUT: a value an agentic replay step reads off the screen and\nreports back (e.g. the receipt number an ERP assigns on Save). The inverse of a\n:class:`SopParam` — declared once per procedure, reported through the healer's ``done`` tool,\nand returned to the caller on the replay result.",
      properties: {
        description: {
          default: "",
          description:
            "What the value is and where it appears, shown to the replay agent.",
          maxLength: 500,
          title: "Description",
          type: "string",
        },
        name: {
          description:
            "Lowercase snake-case output name a step reports its value under.",
          maxLength: 64,
          pattern: "^[a-z][a-z0-9_]*$",
          title: "Name",
          type: "string",
        },
      },
      required: ["name"],
      title: "SopOutput",
      type: "object",
    },
    SopParam: {
      additionalProperties: false,
      description:
        "One ``## Parameters`` entry. ``required`` params render before optional (skill rule).",
      properties: {
        default: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Optional fallback value used when the caller omits the parameter.",
          title: "Default",
        },
        description: {
          description:
            "Human-readable explanation of the parameter's meaning and use.",
          title: "Description",
          type: "string",
        },
        name: {
          description:
            "Lowercase snake-case parameter name referenced by SOP steps.",
          pattern: "^[a-z][a-z0-9_]*$",
          title: "Name",
          type: "string",
        },
        required: {
          description:
            "Whether the caller must bind this parameter before execution.",
          title: "Required",
          type: "boolean",
        },
      },
      required: ["name", "required", "description"],
      title: "SopParam",
      type: "object",
    },
    SopParamsSignal: {
      additionalProperties: false,
      description:
        "Replace the live draft's complete parameter declaration list.",
      properties: {
        kind: {
          const: "sop_params",
          description: "Signal discriminator.",
          title: "Kind",
          type: "string",
        },
        sopParams: {
          description: "Complete parameter list; replace, do not merge.",
          items: {
            $ref: "#/$defs/SopParam",
          },
          title: "Sopparams",
          type: "array",
        },
      },
      required: ["kind", "sopParams"],
      title: "SopParamsSignal",
      type: "object",
    },
    SopPhaseSignal: {
      additionalProperties: false,
      description: "Change the lifecycle phase applied to subsequent captures.",
      properties: {
        kind: {
          const: "sop_phase",
          description: "Signal discriminator.",
          title: "Kind",
          type: "string",
        },
        sopPhase: {
          $ref: "#/$defs/InterviewPhase",
          description: "Active lifecycle phase.",
        },
      },
      required: ["kind", "sopPhase"],
      title: "SopPhaseSignal",
      type: "object",
    },
    SopRetractSignal: {
      additionalProperties: false,
      description: "Remove one provisional step from the live SOP draft.",
      properties: {
        index: {
          description: "One-based draft step index to remove.",
          minimum: 1,
          title: "Index",
          type: "integer",
        },
        kind: {
          const: "sop_retract",
          description: "Signal discriminator.",
          title: "Kind",
          type: "string",
        },
      },
      required: ["kind", "index"],
      title: "SopRetractSignal",
      type: "object",
    },
    SopStep: {
      additionalProperties: false,
      allOf: [
        {
          oneOf: [
            {
              properties: {
                action: {
                  description:
                    "A branch step cannot carry an executable action.",
                  type: "null",
                },
                branch: {
                  $ref: "#/$defs/SopBranch",
                  description:
                    "A branch step must carry its condition and paths.",
                },
                kind: {
                  const: "branch",
                  description:
                    "A structural branch step uses the branch discriminator.",
                },
                target: {
                  description: "A branch step cannot carry an action target.",
                  type: "null",
                },
                verify: {
                  description:
                    "A branch step cannot carry an action post-condition.",
                  type: "null",
                },
              },
              required: ["kind", "branch"],
            },
            {
              properties: {
                branch: {
                  description: "An action step cannot carry a branch payload.",
                  type: "null",
                },
                kind: {
                  const: "action",
                  description:
                    "An executable or agentic action step uses the action discriminator.",
                },
              },
            },
          ],
        },
      ],
      description:
        "One confirmed ``## Steps`` entry: what happened + RFC 2119 constraints + durable target.\n\n``action`` (optional) makes the step **deterministically replayable** — the replayer runs its\ncode directly instead of asking the LLM, faster and exact for stable UI actions (doc 26 §14).\nSteps without an action replay agentically from ``description``.\n\n``step_id`` / ``provenance`` / ``risk`` are interview-session metadata (AI-166.2): ``step_id``\nis the stable mutation address (indices renumber on removal), ``provenance`` distinguishes an\nauto-committed *provisional* step from a user-*confirmed* one, ``risk`` is the effective group\nrisk that produced the step. None of the three render into the markdown — they live only in\nthe snapshot's serialized draft.",
      properties: {
        action: {
          anyOf: [
            {
              $ref: "#/$defs/StepAction",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Optional deterministic replay action; null delegates the step to an agent at run time.",
        },
        authorship: {
          $ref: "#/$defs/StepAuthorship",
          default: "agent",
          description:
            "Whether the agent or a human takeover demonstrated the step.",
        },
        branch: {
          anyOf: [
            {
              $ref: "#/$defs/SopBranch",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Branch condition and paths, present only when kind is branch.",
        },
        constraints: {
          description:
            "Non-empty RFC 2119 requirements that bound safe execution of the step.",
          items: {
            description: "One requirement containing an RFC 2119 keyword.",
            pattern:
              "(?:^|[ \\t\\r\\n.,;:!?()\\[\\]{}'\"/-])(?:MUST NOT|MUST|SHALL NOT|SHALL|SHOULD NOT|SHOULD|MAY|REQUIRED|RECOMMENDED|OPTIONAL)(?:$|[ \\t\\r\\n.,;:!?()\\[\\]{}'\"/-])",
            type: "string",
          },
          minItems: 1,
          title: "Constraints",
          type: "array",
        },
        description: {
          description:
            "Detailed instruction describing what the step accomplishes.",
          title: "Description",
          type: "string",
        },
        index: {
          description: "One-based display position in the current SOP draft.",
          minimum: 1,
          title: "Index",
          type: "integer",
        },
        kind: {
          $ref: "#/$defs/SopStepKind",
          default: "action",
          description:
            "Step discriminator separating executable actions from branch nodes.",
        },
        name: {
          description: "Concise human-readable name of the SOP step.",
          title: "Name",
          type: "string",
        },
        pathId: {
          default: "root",
          description:
            "Demonstrated trajectory path containing the step; root denotes the shared linear path.",
          title: "Pathid",
          type: "string",
        },
        phase: {
          $ref: "#/$defs/InterviewPhase",
          default: "main",
          description:
            "Lifecycle phase in which the step runs: setup, repeatable main work, or teardown.",
        },
        provenance: {
          $ref: "#/$defs/StepProvenance",
          default: "confirmed",
          description:
            "Confirmation lifecycle state distinguishing provisional auto-captures from human-confirmed steps.",
        },
        risk: {
          $ref: "#/$defs/RiskLevel",
          default: "high",
          description:
            "Effective safety risk assigned when the step was captured.",
        },
        stepId: {
          description:
            "Stable mutation and persistence identifier that does not change when display indexes are renumbered.",
          title: "Stepid",
          type: "string",
        },
        surface: {
          $ref: "#/$defs/Surface",
          description:
            "Interaction surface on which the step was demonstrated.",
        },
        target: {
          anyOf: [
            {
              $ref: "#/$defs/StepTarget",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Durable element locator captured for an action step; null for branch or targetless agentic steps.",
        },
        verify: {
          anyOf: [
            {
              $ref: "#/$defs/StepVerify",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Optional post-condition checked after the replay action.",
        },
      },
      required: ["index", "name", "description", "constraints", "surface"],
      title: "SopStep",
      type: "object",
    },
    SopStepKind: {
      description:
        "What a SOP step *is*. An ``action`` step does something on the page (today's only kind —\nthe additive default, so every pre-branch step reads as ``action``). A ``branch`` step does\nnothing itself: it carries a condition + N labelled paths that *bind* ordinary flat action\nsteps by ``step_id`` — replay decides one path at the node and skips the others' members.",
      enum: ["action", "branch"],
      title: "SopStepKind",
      type: "string",
    },
    SopStepSignal: {
      additionalProperties: false,
      description: "A logical SOP step committed to the live draft.",
      properties: {
        kind: {
          const: "sop_step",
          description: "Signal discriminator.",
          title: "Kind",
          type: "string",
        },
        sopStep: {
          $ref: "#/$defs/SopStepView",
          description: "Complete live-view step projection.",
        },
      },
      required: ["kind", "sopStep"],
      title: "SopStepSignal",
      type: "object",
    },
    SopStepView: {
      additionalProperties: false,
      description: "Live, redacted projection of one committed SOP step.",
      properties: {
        codeKind: {
          anyOf: [
            {
              $ref: "#/$defs/CodeKind",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Kind of captured deterministic code, when present.",
        },
        health: {
          anyOf: [
            {
              $ref: "#/$defs/SelectorHealth",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Selector durability assessment, when available.",
        },
        index: {
          description: "One-based position in the live draft.",
          minimum: 1,
          title: "Index",
          type: "integer",
        },
        intent: {
          description: "Redacted description of what the step accomplishes.",
          title: "Intent",
          type: "string",
        },
        name: {
          description: "Human-readable step name.",
          title: "Name",
          type: "string",
        },
        phase: {
          $ref: "#/$defs/InterviewPhase",
          default: "main",
          description: "Lifecycle phase containing the step.",
        },
        risk: {
          $ref: "#/$defs/RiskLevel",
          description: "Effective confirmation risk.",
        },
        status: {
          $ref: "#/$defs/StepProvenance",
          description: "Current authoring provenance.",
        },
      },
      required: ["index", "name", "intent", "risk", "status"],
      title: "SopStepView",
      type: "object",
    },
    StateGraph: {
      additionalProperties: false,
      description:
        "Complete evaluable state-machine graph for a generated mock.",
      properties: {
        authored: {
          default: false,
          description:
            "Whether semantic states were authored instead of inferred.",
          title: "Authored",
          type: "boolean",
        },
        edges: {
          description: "Graph transitions.",
          items: {
            $ref: "#/$defs/GraphStateEdge",
          },
          title: "Edges",
          type: "array",
        },
        nodes: {
          description: "Graph states.",
          items: {
            $ref: "#/$defs/GraphStateNode",
          },
          title: "Nodes",
          type: "array",
        },
        parameters: {
          description: "Runtime parameters shared by all paths.",
          items: {
            $ref: "#/$defs/GraphParameter",
          },
          title: "Parameters",
          type: "array",
        },
        paths: {
          description: "Evaluation traversals.",
          items: {
            $ref: "#/$defs/GraphPath",
          },
          title: "Paths",
          type: "array",
        },
        screens: {
          description: "Storyboard screen labels.",
          items: {
            type: "string",
          },
          title: "Screens",
          type: "array",
        },
        site: {
          default: "",
          description: "Stable mock site slug.",
          title: "Site",
          type: "string",
        },
        version: {
          const: 1,
          default: 1,
          description: "State-graph schema version.",
          title: "Version",
          type: "integer",
        },
      },
      title: "StateGraph",
      type: "object",
    },
    StateGraphArtifact: {
      additionalProperties: false,
      description: "Mock studio state-machine graph.",
      properties: {
        data: {
          $ref: "#/$defs/StateGraph",
          description: "Captured graph states, actions, and traversals.",
        },
        name: {
          const: "state_graph",
          description: "Artifact discriminator.",
          title: "Name",
          type: "string",
        },
      },
      required: ["name", "data"],
      title: "StateGraphArtifact",
      type: "object",
    },
    StepAction: {
      additionalProperties: false,
      description:
        'An optional **deterministic** action for a step — fast, no-LLM replay (doc 26 §14).\n\n``kind="script"`` carries inline Playwright over the live ``page`` (the common case, usually\ncaptured from what the agent just did). ``kind="skill_ref"`` names a reusable browser skill\n— the registry is a future phase, so the field is *reserved* for forward compatibility and not\nexecuted yet. ``kind="credential_fill"`` is a deterministic secret fill: it carries a CSS\n``selector`` + a ``credential_name`` (never the value) that the surface resolves and types\nstraight into the DOM at replay — so a login SOP\'s password/OTP/VM-password fields fill\ndeterministically without an LLM turn, while the secret still never enters ``code``, the\nsandbox, or a screenshot (secrecy invariant §8). A step with **no** action replays\n*agentically* (the LLM performs it from ``description``). NEVER embed a secret value in\n``code`` — a value-carrying credential fill uses ``credential_fill`` instead.',
      properties: {
        code: {
          default: "",
          description:
            "Inline deterministic Playwright snippet for a script action; must never contain secret values.",
          title: "Code",
          type: "string",
        },
        credential: {
          anyOf: [
            {
              $ref: "#/$defs/CredentialFill",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Value-free secure fill descriptor used only by a credential-fill action.",
        },
        kind: {
          $ref: "#/$defs/StepActionKind",
          default: "script",
          description:
            "Action strategy: inline script, reusable skill reference, or secure credential fill.",
        },
        language: {
          $ref: "#/$defs/ScriptLanguage",
          default: "python",
          description: "Language of the inline deterministic script.",
        },
        params: {
          additionalProperties: {
            type: "string",
          },
          default: {},
          description:
            "Static string parameters supplied to a reusable action.",
          title: "Params",
          type: "object",
        },
        skill: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Reserved reusable-skill name for a skill-reference action.",
          title: "Skill",
        },
      },
      title: "StepAction",
      type: "object",
    },
    StepActionKind: {
      description:
        "Deterministic execution strategy stored on an authored SOP step.",
      enum: ["script", "skill_ref", "credential_fill"],
      title: "StepActionKind",
      type: "string",
    },
    StepAuthorship: {
      description: "Actor whose demonstration produced an authored SOP step.",
      enum: ["agent", "human"],
      title: "StepAuthorship",
      type: "string",
    },
    StepFailedError: {
      additionalProperties: false,
      description: "An SOP step could not be reproduced.",
      properties: {
        code: {
          const: "step_failed",
          description: "Error discriminator.",
          title: "Code",
          type: "string",
        },
        message: {
          anyOf: [
            {
              maxLength: 300,
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Redacted detail.",
          title: "Message",
        },
        retryable: {
          const: false,
          default: false,
          description: "Retry policy.",
          title: "Retryable",
          type: "boolean",
        },
      },
      required: ["code"],
      title: "StepFailedError",
      type: "object",
    },
    StepMode: {
      description:
        "Execution or recovery path that produced an SOP replay-step outcome.",
      enum: ["deterministic", "healed", "agentic", "failed"],
      title: "StepMode",
      type: "string",
    },
    StepProvenance: {
      description:
        "Human-confirmation lifecycle state of an authored SOP step.",
      enum: ["confirmed", "provisional"],
      title: "StepProvenance",
      type: "string",
    },
    StepSignal: {
      additionalProperties: false,
      description:
        "One canonical agent action and its optional display conveniences.",
      properties: {
        action: {
          description: "Display-safe action identifier.",
          minLength: 1,
          title: "Action",
          type: "string",
        },
        kind: {
          const: "step",
          description: "Signal discriminator.",
          title: "Kind",
          type: "string",
        },
        liveViewUrl: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Live preview surfaced while this step is active.",
          title: "Liveviewurl",
        },
        operatorStep: {
          $ref: "#/$defs/OperatorStep",
          description: "Authoritative structured action record.",
        },
        section: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Optional UI grouping hint such as setup or finalize.",
          title: "Section",
        },
        subagent: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Nested agent responsible for the step, when applicable.",
          title: "Subagent",
        },
        usage: {
          anyOf: [
            {
              $ref: "#/$defs/OperatorStepTokens",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Per-call token usage duplicated for lightweight consumers.",
        },
      },
      required: ["kind", "action", "operatorStep"],
      title: "StepSignal",
      type: "object",
    },
    StepTarget: {
      additionalProperties: false,
      description:
        "A durable, replayable handle to the element/locator a step acted on (doc 26 §7, §11.3).\n\nPrefer ``a11y_ref`` / ``selector`` / ``code_locator`` over ``visual`` because pixel coordinates\ndo not survive a layout change; a ``visual`` target records a best-effort selector and is\nflagged low-confidence for any future replay.",
      properties: {
        confidence: {
          $ref: "#/$defs/TargetConfidence",
          default: "high",
          description:
            "Confidence that the locator will remain stable across future replays.",
        },
        kind: {
          $ref: "#/$defs/StepTargetKind",
          description:
            "Locator strategy used to identify the target during replay.",
        },
        value: {
          description:
            "Durable locator value, such as an accessibility reference, selector, or code locator.",
          title: "Value",
          type: "string",
        },
      },
      required: ["kind", "value"],
      title: "StepTarget",
      type: "object",
    },
    StepTargetKind: {
      description:
        "Durable locator strategy used to identify a browser element during replay.",
      enum: ["a11y_ref", "selector", "code_locator", "visual"],
      title: "StepTargetKind",
      type: "string",
    },
    StepVerify: {
      additionalProperties: false,
      description:
        "An optional post-condition that proves a step worked (doc 28 §4 P1, AI-166 B2).\n\n``url_contains`` asserts a substring of the page URL after the step; ``text_visible`` asserts\nexact visible text (e.g. a success banner). The deterministic replayer executes the check after\nthe step's action code — a failed check fails the code step and triggers the agentic fallback.\n``python_assert`` evaluates a read-only Python boolean expression over ``page`` and\n``params`` in the configured code runtime, including after healing. False or an exception\nfails verification. Use a single-line expression for Markdown round trips.",
      properties: {
        kind: {
          $ref: "#/$defs/VerifyKind",
          description:
            "Post-condition strategy used to verify that the action succeeded.",
        },
        value: {
          description:
            "Expected URL substring or exact visible text checked after the action.",
          title: "Value",
          type: "string",
        },
      },
      required: ["kind", "value"],
      title: "StepVerify",
      type: "object",
    },
    StoryboardEdge: {
      additionalProperties: false,
      description:
        "One branch in the source-storyboard graph: the user action that moves from screen ``src`` to\nscreen ``to``. ``src``/``to`` are node indices == positions in the ``storyboard`` notes (and\nthe image FileParts). ``from`` on the wire (a Python keyword) maps to ``src``.",
      properties: {
        from: {
          description:
            "Zero-based source-screen index; serialized as `from` on the wire.",
          minimum: 0,
          title: "From",
          type: "integer",
        },
        label: {
          default: "",
          description: "Human-readable user action that causes the transition.",
          title: "Label",
          type: "string",
        },
        to: {
          description:
            "Zero-based destination-screen index reached by this transition.",
          minimum: 0,
          title: "To",
          type: "integer",
        },
      },
      required: ["from", "to"],
      title: "StoryboardEdge",
      type: "object",
    },
    Surface: {
      description:
        "Perception and interaction surface used to capture or replay an SOP step.",
      enum: ["semantic", "hybrid", "visual", "code"],
      title: "Surface",
      type: "string",
    },
    TakeoverResume: {
      additionalProperties: false,
      description:
        "Continue after the human drove the live browser themselves.",
      properties: {
        instruction: {
          default: "",
          description: "Optional guidance describing what the human changed.",
          title: "Instruction",
          type: "string",
        },
        kind: {
          const: "takeover",
          description: "Resume-directive discriminator.",
          title: "Kind",
          type: "string",
        },
      },
      required: ["kind"],
      title: "TakeoverResume",
      type: "object",
    },
    TargetConfidence: {
      description:
        "Producer assessment of how reliably a captured target will survive UI drift.",
      enum: ["high", "low"],
      title: "TargetConfidence",
      type: "string",
    },
    TaskRef: {
      additionalProperties: false,
      description: "Stable A2A coordinates for continuing one parked task.",
      properties: {
        contextId: {
          description:
            "A2A conversation context identifier returned by the server.",
          minLength: 1,
          title: "Contextid",
          type: "string",
        },
        taskId: {
          description: "A2A task identifier returned by the server.",
          minLength: 1,
          title: "Taskid",
          type: "string",
        },
      },
      required: ["taskId", "contextId"],
      title: "TaskRef",
      type: "object",
    },
    TextResume: {
      additionalProperties: false,
      description: "A human's plain-text response to a question pause.",
      properties: {
        kind: {
          const: "answer",
          description: "Resume-directive discriminator.",
          title: "Kind",
          type: "string",
        },
        text: {
          description: "Human response supplied to the paused skill.",
          title: "Text",
          type: "string",
        },
      },
      required: ["kind", "text"],
      title: "TextResume",
      type: "object",
    },
    TrajectoryBranchPoint: {
      additionalProperties: false,
      description:
        "Point at which an alternate trajectory path forks from its parent.",
      properties: {
        conditionLabel: {
          description: "Human-readable condition selecting the alternate path.",
          minLength: 1,
          title: "Conditionlabel",
          type: "string",
        },
        fromFrameIndex: {
          description:
            "Last shared parent frame before the alternate diverges.",
          minimum: 0,
          title: "Fromframeindex",
          type: "integer",
        },
        parentPathId: {
          description: "Parent path identifier.",
          minLength: 1,
          title: "Parentpathid",
          type: "string",
        },
      },
      required: ["parentPathId", "fromFrameIndex", "conditionLabel"],
      title: "TrajectoryBranchPoint",
      type: "object",
    },
    TrajectoryFrame: {
      additionalProperties: false,
      description:
        "One durable capture frame in an authored multi-path walkthrough.",
      properties: {
        gestures: {
          description: "Secret-safe gestures captured since the prior frame.",
          items: {
            $ref: "#/$defs/TrajectoryGesture",
          },
          title: "Gestures",
          type: "array",
        },
        index: {
          description: "One-based global capture index.",
          minimum: 1,
          title: "Index",
          type: "integer",
        },
        pathId: {
          default: "root",
          description: "Demonstrated path containing this frame.",
          minLength: 1,
          title: "Pathid",
          type: "string",
        },
        screenshot: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description:
            "Uploaded screenshot reference when capture storage is configured.",
          title: "Screenshot",
        },
        stepIds: {
          description: "SOP step ids appended or refined by this frame.",
          items: {
            type: "string",
          },
          title: "Stepids",
          type: "array",
        },
        url: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Page URL at capture time.",
          title: "Url",
        },
      },
      required: ["index"],
      title: "TrajectoryFrame",
      type: "object",
    },
    TrajectoryGesture: {
      additionalProperties: false,
      description:
        "One secret-safe browser gesture captured during SOP authoring.",
      properties: {
        name: {
          default: "",
          description: "Redacted accessible name of the target.",
          title: "Name",
          type: "string",
        },
        role: {
          default: "",
          description: "Accessible role of the target element.",
          title: "Role",
          type: "string",
        },
        secret: {
          default: false,
          description: "Whether the original gesture touched a secret value.",
          title: "Secret",
          type: "boolean",
        },
        type: {
          default: "",
          description: "Observed DOM event type.",
          title: "Type",
          type: "string",
        },
        url: {
          default: "",
          description: "Page URL associated with navigation.",
          title: "Url",
          type: "string",
        },
        value: {
          anyOf: [
            {
              type: "string",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Redacted non-secret value associated with the gesture.",
          title: "Value",
        },
        x: {
          anyOf: [
            {
              type: "number",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Optional viewport x coordinate.",
          title: "X",
        },
        y: {
          anyOf: [
            {
              type: "number",
            },
            {
              type: "null",
            },
          ],
          default: null,
          description: "Optional viewport y coordinate.",
          title: "Y",
        },
      },
      title: "TrajectoryGesture",
      type: "object",
    },
    UsageTagNamespace: {
      description:
        "The closed set of tag namespaces both services may emit.\n\nPublished into the contract, so the generated TypeScript union and the runtime\n``enum`` array in the generated schema are the single source of truth for the\nread side's allowlist — nothing re-declares this list.\n\nEmission order is the declaration order here, and it is load-bearing: when the\n``MAX_TAG_COUNT`` cap truncates, it must drop the least valuable tail\ndeterministically rather than an arbitrary subset.",
      enum: [
        "org",
        "user",
        "workflow",
        "node",
        "agent",
        "sop",
        "skill",
        "module",
        "feature",
      ],
      title: "UsageTagNamespace",
      type: "string",
    },
    VerifyKind: {
      description:
        "Machine-verifiable post-condition available after an SOP action.",
      enum: ["url_contains", "text_visible", "python_assert"],
      title: "VerifyKind",
      type: "string",
    },
    ai_service__a2a__author_sop__draft__models__BranchCheck: {
      additionalProperties: false,
      description:
        "A deterministic predicate deciding one branch path at replay time (doc 03-branches).\n\n``url_contains`` / ``text_visible`` probe the live page (mirroring :class:`StepVerify`);\n``param_equals`` compares the run parameter named ``param`` against ``value`` — pure, no page\nprobe. A path with **no** check is the default (evaluated last); all-null checks mean the\nagentic chooser decides.",
      properties: {
        kind: {
          $ref: "#/$defs/BranchCheckKind",
          description:
            "Predicate strategy: inspect the URL, visible text, or a resolved run parameter.",
        },
        param: {
          default: "",
          description:
            "Run-parameter name used only by a param-equals check; empty for page-observation predicates.",
          title: "Param",
          type: "string",
        },
        value: {
          description:
            "Expected page substring or parameter value used by the predicate.",
          title: "Value",
          type: "string",
        },
      },
      required: ["kind", "value"],
      title: "BranchCheck",
      type: "object",
    },
    ai_service__a2a__run_sop__config__models__BranchCheck: {
      additionalProperties: false,
      description:
        "Deterministic predicate deciding whether a branch path applies at replay time — the\nfrozen wire mirror of Node's ``BranchCheckSchema`` (``{kind, value, param}``).\n``url_contains``/``text_visible`` probe the live page; ``param_equals`` compares the run\nparameter named ``param`` against ``value`` (pure, no probe).",
      properties: {
        kind: {
          $ref: "#/$defs/BranchCheckKind",
          description:
            "Predicate discriminator: inspect the current URL, visible text, or a resolved run parameter.",
        },
        param: {
          default: "",
          description:
            "Resolved parameter name read by a param-equals check; empty for page-observation checks.",
          title: "Param",
          type: "string",
        },
        value: {
          default: "",
          description:
            "Expected substring or parameter value used by the predicate.",
          title: "Value",
          type: "string",
        },
      },
      required: ["kind"],
      title: "BranchCheck",
      type: "object",
    },
  },
  $id: "https://gently.com/contracts/a2a/v1/schema.json",
  $schema: "https://json-schema.org/draft/2020-12/schema",
  description:
    "Canonical application objects carried between the Python AI service and typed consumers. The upstream A2A protocol envelope is out of scope.",
  title: "ERP A2A Application Contract v1",
} as const;
